import RemoteCalibrator from '../core'

import {
  _getEyeToCameraCm,
  blindSpotTestNew,
  getLeftAndRightEyePointsFromMeshData,
  knownDistanceTest,
  // objectLengthCmGlobal, objectTest, solveEyeToScreenCm already below
  objectTest,
  solveEyeToScreenCm,
} from './distance'

import {
  objectLengthCmGlobal,
  globalPointXYPx,
} from './object/objectTestOrchestrator'
import {
  estimateHeadYaw,
  correctIpdForHeadRotation,
  estimateHeadYawRobust,
  isHeadRotationCorrectionEnabled,
} from './headYaw'
import { justCreditCard } from './justCreditCard'
import {
  toFixedNumber,
  constructInstructions,
  blurAll,
  sleep,
  safeExecuteFunc,
  median,
  average,
  emptyFunc,
  randn_bm,
  replaceNewlinesWithBreaks,
  getCameraResolutionXY,
  forceFullscreen,
  getCameraXYPx,
  showLoadingVideoMessage,
  hideLoadingVideoMessage,
} from '../components/utils'
import { iRepeat } from '../components/iRepeat'
import { ensureVideoPlaying } from './ensureVideoPlaying'
import { phrases } from '../i18n/schema'
import { spaceForLanguage } from '../components/language'
import { checkPermissions } from '../components/mediaPermission'
import Swal from 'sweetalert2'
import { swalInfoOptions } from '../components/swalOptions'
import { setUpEasyEyesKeypadHandler } from '../extensions/keypadHandler'
import {
  showTestPopup,
  hideResolutionSettingMessage,
} from '../components/popup'
import { processInlineFormatting } from './markdownInstructionParser'
import { objectTestNew } from './object'

// A divergent face-pose frame (lateral offset = tan of yaw → ∞) computes a
// non-finite on-screen position. Published tracking data must carry either a
// finite 2-vector or null — "no valid estimate this frame" — so consumers
// never store garbage. Never substitute a stale position: distanceCm etc.
// remain raw, and a fabricated position would contradict them.
export const finitePairOrNull = pair =>
  Array.isArray(pair) &&
  pair.length === 2 &&
  Number.isFinite(pair[0]) &&
  Number.isFinite(pair[1])
    ? [pair[0], pair[1]]
    : null
import {
  armTubeTracker,
  configureTubeTracker,
  disarmTubeTracker,
  getFaceMeshVideoInput,
  getTubeCropMode,
  normalizeExcludeTubeMode,
  renderTubeCropDisplay,
  renderTubeTrackerDebug,
  setTubeGuideQuad,
  tubeTrackerForcesRed,
  updateTubeTracker,
} from './object/tubeTemplateTracker'

const originalStyles = {
  video: false,
}

// Pre-calibration popup similar to equipment popup
const showPreCalibrationPopup = async RC => {
  // Hide the resolution setting message before showing this popup
  hideResolutionSettingMessage()

  const html = `
    <p style="text-align: left; margin-top: 1rem; font-size: 1.4rem; line-height: 1.6;">
      ${processInlineFormatting(phrases.RC_IsCameraTopCenter[RC.L]).replace('\n', '<br />').replace('\n', '<br />')}
    </p>
    <div id="custom-radio-group">
      <label>
        <input type="radio" name="calibration-method" value="Yes" />
        ${phrases.RC_Yes[RC.L]}
      </label>
      <label>
        <input type="radio" name="calibration-method" value="No" />
        ${phrases.RC_No[RC.L]}
      </label>
      <label>
        <input type="radio" name="calibration-method" value="DontKnow" />
        ${phrases.RC_DontKnow[RC.L]}
      </label>
    </div>
  `

  const { value: result } = await Swal.fire({
    ...swalInfoOptions(RC, {
      showIcon: false,
    }),
    html,
    preConfirm: () => {
      const selected = document.querySelector(
        'input[name="calibration-method"]:checked',
      )
      if (!selected) {
        Swal.showValidationMessage(
          phrases.RC_PleaseSelectAnOption[RC.language.value],
        )
        return null
      }
      return selected.value
    },
    didOpen: () => {
      const customInputs = document.querySelectorAll(
        'input[name="calibration-method"]',
      )
      const keydownListener = event => {
        if (event.key === 'Enter') {
          Swal.clickConfirm() // Simulate the "OK" button click
        }
      }

      customInputs.forEach(input => {
        input.addEventListener('keyup', keydownListener)
      })

      if (RC.keypadHandler) {
        const removeKeypadHandler = setUpEasyEyesKeypadHandler(
          null,
          RC.keypadHandler,
          () => {
            removeKeypadHandler()
            Swal.clickConfirm()
          },
          false,
          ['return'],
          RC,
        )
      }

      // Store listeners for cleanup
      RC.customKeydownListener = keydownListener
      RC.customInputs = customInputs
    },
    willClose: () => {
      // Remove keydown event listeners when the modal closes
      if (RC.customInputs) {
        RC.customInputs.forEach(input => {
          input.removeEventListener('keyup', RC.customKeydownListener)
        })
      }
    },
  })

  if (!result) return null

  // Store the selected option for potential future use
  RC.preCalibrationChoice = result

  return result
}

RemoteCalibrator.prototype.trackDistance = async function (
  trackDistanceOptions = {},
  callbackStatic = undefined,
  callbackTrack = undefined,
) {
  /**
   * options -
   *
   * fullscreen: [Boolean]
   * repeatTesting: 1
   * pipWidthPx: [208]
   *
   * (Interface)
   * headline: [String]
   * description: [String]
   *
   */

  ////
  if (!this.checkInitialized()) return
  blurAll()
  ////

  if (this.gazeTracker.webgazer.getTracker().modelLoaded === false) {
    // inform the user that the model is still loading
    // Swal.fire({
    //   ...swalInfoOptions(this, { showIcon: false }),
    //   title: phrases.EE_FaceMeshLoading[this.L],
    //   showConfirmButton: false,
    //   allowOutsideClick: false,
    //   didOpen: () => {
    //     Swal.showLoading()
    //   },
    // })

    // Warm the model in the background. The catch is required: an unhandled
    // rejection here reaches PsychoJS's onunhandledrejection handler, which
    // aborts the whole experiment. The model is loaded again (and awaited)
    // where it is actually needed.
    this.gazeTracker.webgazer
      .getTracker()
      .loadModel()
      .catch(error => {
        console.error('[RC] Background face-model load failed:', error)
      })
    // Swal.close()
  }

  let description
  if (
    trackDistanceOptions.control !== undefined &&
    trackDistanceOptions.control === false
  )
    description = processInlineFormatting(
      phrases.RC_viewingDistanceIntroLiMethod[this.L],
    )
  else
    description = processInlineFormatting(
      phrases.RC_viewingDistanceIntroLiMethod[this.L],
    )

  const options = Object.assign(
    {
      fullscreen: false,
      repeatTesting: 1,
      objectMeasurementCount: 2, // Number of repeated ruler measurements for object test (DEPRECATED: use calibrateDistanceLocations instead)
      calibrateDistanceLocations: ['camera', 'center'], // Array of locations for distance calibration. Each can be: camera, center, topCenter, topOffsetLeft, topOffsetRight, topOffsetDown
      calibrateDistanceOffsetCm: 4, // Offset in cm for topOffsetLeft/Right/Down locations
      calibrateDistanceTubeDiameterCm: 3.5, // Tube outer diameter in cm (used for small circle on video and big circle on screen)
      calibrateDistanceDrawPaperTubeBool: true, // Draw the paper tube visualization (two circles connected by tangent lines)
      calibrateDistanceAllowedRatioCm: 1.1, // Ratio threshold - measurements must satisfy max(M1/M2, M2/M1) <= max(threshold, 1/threshold)
      sparkle: true,
      pipWidthPx:
        this._CONST.N.VIDEO_W[this.isMobile.value ? 'MOBILE' : 'DESKTOP'],
      showVideo: true,
      showFaceOverlay: false,
      decimalPlace: 1,
      framerate: 6, // tracking rate - increased from 3 for better performance
      desiredDistanceCm: undefined,
      desiredDistanceTolerance: 1.2,
      desiredDistanceMonitor: false,
      desiredDistanceMonitorCancelable: false,
      desiredDistanceMonitorAllowRecalibrate: true,
      nearPoint: true,
      showNearPoint: false,
      control: true, // CONTROL (EasyEyes) or AUTOMATIC (Li et al., 2018)
      headline: `${phrases.RC_distanceTrackingTitle[this.L]}`,
      description:
        description +
        spaceForLanguage(this.L) +
        phrases.RC_distanceTrackingIntroEnd[this.L],
      check: false,
      checkCallback: null,
      showCancelButton: true,
      callbackStatic,
      useObjectTestData: false, // New option to use object test data
      objecttestdebug: false, // New option to show debug feedback div in object test
      calibrateDistanceAllowedRatioFOverWidth: 1.1,
      calibrateDistanceAllowedRatioCm: 1.2, // Tube check full-size acceptance threshold
      calibrateDistanceAllowedRatioHalfCm: 1.4, // Tube check half-size acceptance threshold
      calibrateDistanceAllowedRangeCm: [30, 70],
      resolutionWarningThreshold: undefined,
      calibrateDistanceSpotCm: 0.5,
      calibrateDistanceBlindspotDiameterDeg: 2.0,
      calibrateDistanceSpotXYDeg: [15.5, -1.5],
      viewingDistanceWhichEye: undefined,
      viewingDistanceWhichPoint: undefined,
      calibrateDistanceBlindspotDebugging: false, // Debug option to show anatomical line and diamond center
      calibrateDistanceChecking: undefined,
      calibrateDistanceCheckMinRulerCm: 0,
      calibrateDistanceShowRulerUnitsBool: false,
      calibrateDistancePupil: 'iris',
      calibrateDistanceQuadBaseRatio: 2.0, // Default ratio for quadrilateral base
      calibrateDistanceIpdUsesZBool: true, // Use z coordinate from FaceMesh in IPD calculation (false = 2D only)
      calibrateDistanceCameraResolution: [640, 480], // [width, height] e.g. [1920, 1080] — desired camera resolution
      calibrateDistanceCameraHz: 60, // number e.g. 30 — desired camera frame rate
      saveSnapshots: false,
      calibrateDistanceFocalLengthRange: null, // fOverWidth range for "typical" mode; mean is used
      calibrateDistanceCorrectForHeadRotation: 'none', // 'none' | 'useZ' — how to estimate head yaw to correct ipdOverWidth
      viewingDistanceAllowedHeadRotationDeg: 180, // abs(yaw) above this value triggers the "face the screen" nudger
      _showCameraResolutionBool: true, // If true, show original+final resolution with OK button after camera selection
      calibrateDistanceAcceptBottomCameraBool: false, // If true, also show a duplicate row of camera previews fixed at the bottom of the screen on Choose Camera / Choose Screen pages, so participants whose built-in camera is at the bottom-center of the display can pick the video that mirrors them. When the participant clicks a bottom-row preview, cameraXYPx flips to bottom-center and the PiP / Camera Resolution preview / blindspot fixation / etc. all anchor to the bottom edge.
      calibrateDistanceAllowExternalCameraBool: false, // If true, Choose Camera includes external webcams alongside built-in / unknown. Default false hides externals so trackDistance only ever runs on a built-in (or unclassified) camera. Set true to test bottom-camera support with an external rig.
      _calibrateDistanceExcludeTubeDebugBool: true, // FOR TESTING (default FALSE). When TRUE, shows a real-time debug panel (top-right) and overlay annotations while excluding the paper tube. Display-only. Also: window.rcTubeDebug(true) / window.rcTubeDebug(false).
      _calibrateDistanceExcludeTube: 'excludeHand', // FOR TESTING (default 'doNothing'). 'doNothing' | 'excludeHand' | 'crop' | 'cropButShowWhole'. While the paper tube is likely in FaceMesh's image, disable Space (eyes red) so a biased distance is not saved. 'doNothing' = off. 'excludeHand' (recommended) = MediaPipe Hands on the full camera frame; red while a hand is detected. 'crop' / 'cropButShowWhole' = LEGACY tube-tracking + FaceMesh crop.
      _calibrateDistanceExcludeTubeTweak: '', // excludeHand only. Comma-separated: checkMs, minScore, holdMs, lite. Empty = 150,0.3,1500,0. Raise checkMs (e.g. 250) to cut CPU on slow computers. holdMs = keep eyes red this long after last hand sighting. lite 0=full model (better on partial hands), 1=lite (cheaper). Named form also ok: checkMs=250,lite=1.
      calibrateDistanceCameraKindOverride: 'assess', // FOR TESTING (glossary name `_calibrateDistanceCameraKindOverride`; either spelling accepted). Override the kind classification of the SELECTED camera so EasyEyes' handling of the three kinds can be tested without physically having one of each. 'assess' (default) = classify as usual based on the camera name. 'built-in' / 'external' / 'unknown' = skip assessment and force that kind for the camera the participant commits to. On the Choose Camera page each tile still shows its REAL classification by default; hovering a tile temporarily previews the override kind in that tile's caption (and reverts on mouseleave) -- this is display-only and does not mutate the camera list. The override is committed only when the participant actually selects a camera; if they return and pick another, the previous selection reverts to its real kind. The selection (and its overridden kind) persists across pages for the rest of the session. NOTE: When tabulating camera-kind data, exclude results collected with anything other than 'assess'.
    },
    trackDistanceOptions,
  )

  try {
    this.viewingDistanceAllowedPreciseBool =
      trackDistanceOptions.viewingDistanceAllowedPreciseBool
  } catch (e) {
    this.viewingDistanceAllowedPreciseBool = false
  }

  /* -------------------------------------------------------------------------- */

  // Force fullscreen unconditionally on "Set your viewing distance" page arrival
  forceFullscreen(this.L, this)

  if (
    this.gazeTracker.checkInitialized('distance') &&
    this._distanceTrackingFullyInitialized
  ) {
    // Already fully initialized (re-tracking case) — just reconfigure
    this.gazeTracker._toFixedN = options.decimalPlace
    this.showNearPoint(options.showNearPoint)
    this.showVideo(options.showVideo)
    this.showFaceOverlay(options.showFaceOverlay)

    // TODO Attach new callbackTrack
    return
  }

  // Always show a single, centered "Loading video ..." message as soon
  this._addBackground()
  showLoadingVideoMessage(this)

  // Skip the camera-permission prompt if camera selection already ran
  // (e.g. before the panel in the improved flow).
  if (!this._cameraSelectionDone) {
    let message = `${phrases.RC_requestCamera[this.L]}`
    if (!options.saveSnapshots) {
      message += `<br />${phrases.RC_privacyCamera[this.L]}`
    }
    await checkPermissions(this, message)
  }
  ////

  // STEP 2 - Live estimate
  const getStdDist = (distData, excecuteCallbackStaticHere = true) => {
    this.showVideo(originalStyles.video)
    originalStyles.video = false

    if (this.gazeTracker.checkInitialized('gaze', false))
      this.showGazer(originalGazer)

    console.log('=== Setting up tracking with measurement data ===')
    console.log('Distance data:', {
      value: distData.value,
      method: distData.method,
      calibrationFactor: distData.calibrationFactor,
      averageFaceMesh: distData.averageFaceMesh,
    })
    console.log('================================================')

    // Validate that we have a calibration factor
    if (!distData.calibrationFactor) {
      console.error('ERROR: No calibration factor found in measurement data!')
      console.error(
        'This means the measurement test did not properly calculate the calibration factor.',
      )
      console.error('Measurement data:', distData)
      return
    }

    console.log(
      '=== CALLING CALLBACK STATIC ===',
      excecuteCallbackStaticHere,
      distData,
      callbackStatic,
    )
    if (excecuteCallbackStaticHere) safeExecuteFunc(callbackStatic, distData)
    stdDist.current = distData
    stdDist.method = distData.method
    stdFactor = null
  }

  /* -------------------------------------------------------------------------- */

  // STEP 1 - Calibrate for live estimate
  const originalGazer = this.gazeTracker.webgazer.params.showGazeDot
  const _ = async () => {
    // Only show blindspot instruction screen if we're using blindspot test only
    // For object test only or 'both' mode, skip the instruction screen and go directly to the test
    if (!options.useObjectTestData) {
      // The blindspot intro headline is the next page -- drop the
      // centered "Loading video ..." message before it renders so the
      // two don't overlap.
      hideLoadingVideoMessage()

      this._addBackground()

      this._replaceBackground(
        constructInstructions(options.headline, null, true, ''),
      )

      if (this.gazeTracker.checkInitialized('gaze', false))
        this.showGazer(false)
    }

    // Show camera selection popup (skip if already done before the panel)
    if (this._cameraSelectionDone) {
      console.log('=== Camera selection already completed — skipping ===')
    } else {
      console.log('=== Checking for camera selection ===')
      const cameraResult = await showTestPopup(this, null, options)
      if (cameraResult?.experimentEnded) {
        console.log('Experiment ended - no cameras detected')
      }
      this._cameraSelectionDone = true
    }

    // Mark that camera selection has been done to avoid calling it again in calibration methods
    options.cameraSelectionDone = true

    console.log('showIrisesBool', options.showIrisesBool)
    //calibrateDistancePupil = iris, eyeCorners
    if (options.calibrateDistancePupil === 'eyeCorners') {
      // set the gaze tracker to TFFacemesh_unrefined_landmarks
      await this.gazeTracker.webgazer.setTracker(
        'TFFacemesh_unrefined_landmarks',
      )
    }
    // Start iris drawing with mesh data before calibration tests
    // Skip for justCreditCard and autoCreditCard modes
    if (
      options.showIrisesBool &&
      options.useObjectTestData !== 'justCreditCard' &&
      options.useObjectTestData !== 'autoCreditCard'
    ) {
      console.log('=== Starting iris drawing before calibration tests ===')
      await startIrisDrawingWithMesh(this)
    }

    // Show pre-calibration popup before starting any calibration methods
    //only show if calibrateDistanceIsCameraTopCenterBool is true
    if (options.calibrateDistanceIsCameraTopCenterBool) {
      hideLoadingVideoMessage()
      const preCalibrationResult = await showPreCalibrationPopup(this)
      if (!preCalibrationResult) {
        // User cancelled or didn't select anything, exit gracefully
        console.log('Pre-calibration popup cancelled by user')
        return
      }
    }

    // Check if we should use object test data
    // DEBUG: Log calibrateDistanceLocations before calling test functions
    console.log('=== distanceTrack: Before calling test function ===')
    console.log(
      'options.calibrateDistanceLocations:',
      options.calibrateDistanceLocations,
    )
    console.log('options.useObjectTestData:', options.useObjectTestData)

    if (options.useObjectTestData === 'both') {
      console.log('=== Starting Both Methods Test ===')
      console.log(
        'This will run object test first, then blindspot test, then use median calibration factor',
      )
      // First run object test
      await objectTestNew(this, options, getStdDist)
      // await objectTest(this, options, getStdDist)
    } else if (options.useObjectTestData === 'creditCard') {
      // Call knownDistanceTest directly for calibration
      hideLoadingVideoMessage()
      await knownDistanceTest(this, options, getStdDist)
    } else if (options.useObjectTestData === 'autoCreditCard') {
      hideLoadingVideoMessage()
      await justCreditCard(this, options, getStdDist)
    } else if (options.useObjectTestData === 'justCreditCard') {
      hideLoadingVideoMessage()
      await justCreditCard(this, options, getStdDist)
    } else if (options.useObjectTestData) {
      console.log('=== Starting Object Test Only ===')
      console.log('This will use object test calibration factor for tracking')
      // Call objectTest directly for calibration
      await objectTestNew(this, options, getStdDist)
      // await objectTest(this, options, getStdDist)
    } else {
      console.log('=== Starting Blindspot Test Only ===')
      console.log(
        'This will use blindspot test calibration factor for tracking',
      )
      // Use blindspot test for calibration
      await blindSpotTestNew(this, options, true, getStdDist)
    }
  }

  trackingOptions.pipWidthPx = options.pipWidthPx
  trackingOptions.decimalPlace = options.decimalPlace
  trackingOptions.framerate = options.framerate
  trackingOptions.nearPoint = options.nearPoint
  trackingOptions.showNearPoint = options.showNearPoint
  trackingOptions.showIrisesBool = options.showIrisesBool
  trackingOptions.useObjectTestData = options.useObjectTestData
  trackingOptions.showNearestPointsBool = options.showNearestPointsBool
  trackingOptions.calibrateDistanceShowRulerUnitsBool =
    options.calibrateDistanceShowRulerUnitsBool
  trackingOptions.objectMeasurementCount = options.objectMeasurementCount
  trackingOptions.calibrateDistanceLocations =
    options.calibrateDistanceLocations
  trackingOptions.calibrateDistanceAllowedRatioCm =
    options.calibrateDistanceAllowedRatioCm
  trackingOptions.calibrateDistancePupil = options.calibrateDistancePupil
  trackingOptions.calibrateDistanceIpdUsesZBool =
    options.calibrateDistanceIpdUsesZBool
  trackingOptions.calibrateDistanceTubeDiameterCm =
    options.calibrateDistanceTubeDiameterCm
  trackingOptions.calibrateDistanceDrawPaperTubeBool =
    options.calibrateDistanceDrawPaperTubeBool
  // _calibrateDistanceExcludeTube
  {
    const rawExcludeTube =
      trackDistanceOptions._calibrateDistanceExcludeTube ??
      trackDistanceOptions.calibrateDistanceExcludeTube ??
      options._calibrateDistanceExcludeTube
    const normalizedExclude = normalizeExcludeTubeMode(rawExcludeTube)
    if (
      rawExcludeTube !== undefined &&
      rawExcludeTube !== null &&
      String(rawExcludeTube).trim() !== '' &&
      normalizedExclude === null
    )
      console.warn(
        `_calibrateDistanceExcludeTube: unrecognized value "${rawExcludeTube}", using "doNothing"`,
      )
    const excludeTube = normalizedExclude || 'doNothing'
    options._calibrateDistanceExcludeTube = excludeTube
    trackingOptions._calibrateDistanceExcludeTube = excludeTube
    const excludeTubeDebugBool =
      (trackDistanceOptions._calibrateDistanceExcludeTubeDebugBool ??
        trackDistanceOptions.calibrateDistanceExcludeTubeDebugBool ??
        options._calibrateDistanceExcludeTubeDebugBool) === true
    options._calibrateDistanceExcludeTubeDebugBool = excludeTubeDebugBool
    const rawTweak =
      trackDistanceOptions._calibrateDistanceExcludeTubeTweak ??
      trackDistanceOptions.calibrateDistanceExcludeTubeTweak ??
      options._calibrateDistanceExcludeTubeTweak ??
      ''
    options._calibrateDistanceExcludeTubeTweak = rawTweak
    configureTubeTracker({
      cropMode: excludeTube,
      debugBool: excludeTubeDebugBool || undefined,
      tweak: rawTweak,
    })
  }
  trackingOptions.calibrateDistanceFocalLengthRange =
    options.calibrateDistanceFocalLengthRange
  // Store on RC instance for access from other modules
  this.calibrateDistanceIpdUsesZBool =
    options.calibrateDistanceIpdUsesZBool !== false
  trackingOptions.desiredDistanceCm = options.desiredDistanceCm
  trackingOptions.desiredDistanceTolerance = options.desiredDistanceTolerance
  trackingOptions.desiredDistanceMonitor = options.desiredDistanceMonitor
  trackingOptions.desiredDistanceMonitorCancelable =
    options.desiredDistanceMonitorCancelable
  trackingOptions.desiredDistanceMonitorAllowRecalibrate =
    options.desiredDistanceMonitorAllowRecalibrate

  trackingOptions.viewingDistanceWhichEye = options.viewingDistanceWhichEye
  trackingOptions.viewingDistanceWhichPoint = options.viewingDistanceWhichPoint

  trackingOptions.calibrateDistanceCorrectForHeadRotation =
    options.calibrateDistanceCorrectForHeadRotation

  // Enable the head-rotation nudger for the entire distance session,
  // including the calibration phase (object / blindspot / credit card).
  // The nudger only shows when yaw estimation is enabled and the allowed
  // rotation threshold is below 180° (the "ignore" value).
  this._distanceTrackNudging.headRotationCorrectingEnabled =
    isHeadRotationCorrectionEnabled(
      options.calibrateDistanceCorrectForHeadRotation,
    )
  this.setViewingDistanceAllowedHeadRotationDeg(
    options.viewingDistanceAllowedHeadRotationDeg,
  )
  this._distanceTrackNudging.headYawDeg = 0

  originalStyles.video = options.showVideo

  this.gazeTracker._init(
    {
      toFixedN: 1,
      showVideo: true,
      showFaceOverlay: options.showFaceOverlay,
      desiredCameraResolution: options.calibrateDistanceCameraResolution,
      desiredCameraHz: options.calibrateDistanceCameraHz,
    },
    'distance',
  )

  this._trackingSetupFinishedStatus.distance = false
  this._distanceTrackingFullyInitialized = true

  const trackingConfig = {
    options: options,
    callbackStatic: callbackStatic,
    callbackTrack: callbackTrack,
  }

  if (options.nearPoint) {
    startTrackingPupils(
      this,
      () => {
        return this._measurePD({}, _)
      },
      callbackTrack,
      trackingConfig,
    )
  } else {
    startTrackingPupils(this, _, callbackTrack, trackingConfig)
  }
}

RemoteCalibrator.prototype.setViewingDistanceAllowedPreciseBool = function (
  value = true,
) {
  this.viewingDistanceAllowedPreciseBool = value
}

/**
 * Set the allowed head-rotation (yaw) threshold, in degrees, used by the
 * "face the screen" nudger. When the absolute yaw exceeds this value the
 * nudger is shown; 180 (the default) effectively disables it.
 *
 * Called internally by `trackDistance()` to seed the value from
 * `options.viewingDistanceAllowedHeadRotationDeg`, but can also be called
 * at any time to change the threshold while tracking is live.
 *
 * @param {number} value Allowed absolute yaw in degrees. Non-finite values fall back to 180.
 */
RemoteCalibrator.prototype.setViewingDistanceAllowedHeadRotationDeg = function (
  value = 180,
) {
  const deg = Number.isFinite(value) ? value : 180
  this._viewingDistanceAllowedHeadRotationDeg = deg
  trackingOptions.viewingDistanceAllowedHeadRotationDeg = deg
  if (this._distanceTrackNudging)
    this._distanceTrackNudging.headRotationAllowedDeg = deg
}

/* -------------------------------------------------------------------------- */

const startTrackingPupils = async (
  RC,
  beforeCallbackTrack,
  callbackTrack,
  trackingConfig,
) => {
  // Called fire-and-forget from trackDistance, so this function must not
  // throw: an unhandled rejection reaches PsychoJS's onunhandledrejection
  // handler, which aborts the whole experiment.
  try {
    await RC.gazeTracker.startCameraSession({
      videoOnly: true,
      pipWidthPx: trackingOptions.pipWidthPx,
      requireModel: true,
    })
  } catch (error) {
    console.error(
      '[RC] Camera session failed; cannot start distance tracking:',
      error,
    )
    return
  }

  RC._removeFloatInstructionElement()
  safeExecuteFunc(beforeCallbackTrack)
  _tracking(RC, trackingOptions, callbackTrack, trackingConfig)
}

const eyeDist = (a, b, useZ = true) => {
  if (useZ) {
    // console.log('[distanceTrack.js] eyeDist using 3D formula (with Z)')
    return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z)
  }
  // console.log('[distanceTrack.js] eyeDist using 2D formula (NO Z)')
  return Math.hypot(a.x - b.x, a.y - b.y)
}

const cyclopean = (video, a, b) => {
  return [
    (-a.x - b.x + video.videoWidth) / 2,
    (-a.y - b.y + video.videoHeight) / 2,
  ]
}

/* -------------------------------------------------------------------------- */
const trackingOptions = {
  pipWidthPx: 0,
  decimalPlace: 2,
  framerate: 30,
  nearPoint: true,
  showNearPoint: false,
  desiredDistanceCm: undefined,
  desiredDistanceTolerance: 1.2,
  desiredDistanceMonitor: false,
  desiredDistanceMonitorCancelable: false,
  desiredDistanceMonitorAllowRecalibrate: true,
  calibrateDistanceCorrectForHeadRotation: 'none',
  viewingDistanceAllowedHeadRotationDeg: 180,
}

export const stdDist = {
  current: null,
  method: null, // Track which method was used
}

let stdFactor = null
let video = null
let viewingDistanceTrackingFunction = null
const iRepeatOptions = { framerate: 20, break: true }

let nearPointDot = null
/* -------------------------------------------------------------------------- */

// Canvas-based iris and pupil drawing
let irisCanvas = null
let irisCtx = null
let irisRafId = null
let RC_instance = null // Store RC instance for independent data access
let sharedFaceData = null // Shared face data between tracking and iris drawing
export let irisTrackingIsActive = false // Reflects whether tracking is currently active
let lastIrisValidTime = 0 // Timestamp of last valid face mesh
const IRIS_VALIDITY_WINDOW_MS = 200 // Consider tracking active if we saw valid mesh within this shorter window

// ========== Measurement page overlay state (eye-side text + paper tube circles) ==========
// Set from distance.js when a measurement page is shown; cleared when leaving.
let measurementOverlayState = null
// measurementOverlayState = {
//   isPaperMode: boolean,
//   eye: 'left' | 'right' | 'unspecified',
//   leftTextWords: string[] | null,   // Words for left side text (one per line)
//   rightTextWords: string[] | null,  // Words for right side text (one per line)
// }

/**
 * Configure the measurement overlay drawn on the iris canvas.
 * @param {object} config - { isPaperMode, eye, leftTextWords, rightTextWords,
 *                            enableTubeTracking }
 */
export function setMeasurementOverlay(config) {
  measurementOverlayState = config
  // Arm exclude-tube on paper measurement pages. 'doNothing' never arms.
  if (
    config?.isPaperMode &&
    config?.enableTubeTracking &&
    getTubeCropMode() !== 'doNothing'
  ) {
    armTubeTracker(RC_instance, {
      eye: config.eye,
      tubeCm: trackingOptions.calibrateDistanceTubeDiameterCm,
      ipdCm: RC_instance?._CONST?.IPD_CM,
    })
  } else {
    disarmTubeTracker()
  }
}

/**
 * Remove the measurement overlay.
 */
export function clearMeasurementOverlay() {
  measurementOverlayState = null
  disarmTubeTracker()
}

// FPS throttling variables
const targetFPS = 30
const frameInterval = 1000 / targetFPS
let lastFrameTime = 0
let lastIrisFrameTime = 0
let lastIrisTrackingTime = 0

const createIrisCanvas = () => {
  if (irisCanvas) return irisCanvas

  irisCanvas = document.createElement('canvas')
  irisCanvas.id = 'rc-iris-overlay'
  irisCanvas.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    pointer-events: none;
    z-index: 2147483647;
  `

  // Set canvas size to match viewport
  irisCanvas.width = window.innerWidth
  irisCanvas.height = window.innerHeight

  document.body.appendChild(irisCanvas)
  irisCtx = irisCanvas.getContext('2d')

  // Handle window resize
  window.addEventListener('resize', () => {
    if (irisCanvas) {
      irisCanvas.width = window.innerWidth
      irisCanvas.height = window.innerHeight
    }
  })

  return irisCanvas
}

const drawIrisAndPupil = () => {
  if (!irisCtx) return

  // Clear canvas
  irisCtx.clearRect(0, 0, irisCanvas.width, irisCanvas.height)

  // Find video element for positioning (needed by both iris and overlay drawing)
  const videoFeed = document.getElementById('webgazerVideoFeed')
  const videoContainer = document.getElementById('webgazerVideoContainer')
  const videoEl = videoFeed || videoContainer
  const videoRect = videoEl ? videoEl.getBoundingClientRect() : null

  // Track screen-space eye positions for overlay drawing
  let leftPxScreen = null
  let rightPxScreen = null
  let ipdScreenPx = 0

  // Draw irises if enabled and data available
  if (
    RC_instance &&
    trackingOptions.showIrisesBool &&
    sharedFaceData &&
    videoEl
  ) {
    const { leftEye, rightEye, video, currentIPDDistance, ipdShrinkage } =
      sharedFaceData
    const correctedIPD = correctIpdForHeadRotation(
      currentIPDDistance,
      ipdShrinkage,
    )

    if (leftEye && rightEye && video && correctedIPD && videoRect) {
      const rect = videoRect

      // Source coordinate space - use LIVE camera resolution, not stale canvas dimensions
      const cameraRes = getCameraResolutionXY(RC_instance)
      const srcW = cameraRes[0] || video.width
      const srcH = cameraRes[1] || video.height

      // Account for CSS object-fit by computing scale and crop/letterbox offsets
      const containerW = rect.width
      const containerH = rect.height
      const scaleCover = Math.max(containerW / srcW, containerH / srcH)
      const uniformScale = scaleCover
      const offsetX = (srcW * uniformScale - containerW) / 2
      const offsetY = (srcH * uniformScale - containerH) / 2

      // Apply horizontal flip since video is typically mirrored
      const leftEyeXFlipped = srcW - leftEye.x
      const rightEyeXFlipped = srcW - rightEye.x

      const leftPx = {
        x: rect.left + leftEyeXFlipped * uniformScale - offsetX,
        y: rect.top + leftEye.y * uniformScale - offsetY,
      }
      const rightPx = {
        x: rect.left + rightEyeXFlipped * uniformScale - offsetX,
        y: rect.top + rightEye.y * uniformScale - offsetY,
      }

      // Compute iris diameter from IPD (19%) in source pixels, then scale to CSS
      const ipdSrcPx =
        correctedIPD ||
        Math.hypot(rightEye.x - leftEye.x, rightEye.y - leftEye.y)
      ipdScreenPx = ipdSrcPx * uniformScale
      const irisDiameter = Math.max(4, 0.19 * ipdScreenPx)
      const pupilDiameter = Math.max(2, 0.4 * irisDiameter)
      const irisRadius = irisDiameter / 2
      const pupilRadius = pupilDiameter / 2

      // Choose iris color based on tracking status
      const irisFillColor = irisTrackingIsActive ? '#00ffe9' : '#ff3b30'

      // Draw left iris
      irisCtx.beginPath()
      irisCtx.arc(leftPx.x, leftPx.y, irisRadius, 0, 2 * Math.PI)
      irisCtx.fillStyle = irisFillColor
      irisCtx.fill()

      // Add iris shadow effect
      irisCtx.beginPath()
      irisCtx.arc(leftPx.x, leftPx.y, irisRadius, 0, 2 * Math.PI)
      irisCtx.strokeStyle = 'rgba(0,0,0,0.35)'
      irisCtx.lineWidth = 2
      irisCtx.stroke()

      // Draw left pupil
      irisCtx.beginPath()
      irisCtx.arc(leftPx.x, leftPx.y, pupilRadius, 0, 2 * Math.PI)
      irisCtx.fillStyle = '#000'
      irisCtx.fill()

      // Draw right iris
      irisCtx.beginPath()
      irisCtx.arc(rightPx.x, rightPx.y, irisRadius, 0, 2 * Math.PI)
      irisCtx.fillStyle = irisFillColor
      irisCtx.fill()

      // Add iris shadow effect
      irisCtx.beginPath()
      irisCtx.arc(rightPx.x, rightPx.y, irisRadius, 0, 2 * Math.PI)
      irisCtx.strokeStyle = 'rgba(0,0,0,0.35)'
      irisCtx.lineWidth = 2
      irisCtx.stroke()

      // Draw right pupil
      irisCtx.beginPath()
      irisCtx.arc(rightPx.x, rightPx.y, pupilRadius, 0, 2 * Math.PI)
      irisCtx.fillStyle = '#000'
      irisCtx.fill()

      // Store screen-space positions for overlay drawing
      leftPxScreen = leftPx
      rightPxScreen = rightPx
    }
  }

  // ========== Draw measurement overlay (eye-side text + paper tube circles) ==========
  if (measurementOverlayState && videoRect) {
    _drawMeasurementOverlay(videoRect, leftPxScreen, rightPxScreen, ipdScreenPx)
  }

  // ========== Exclude-tube display (_calibrateDistanceExcludeTube) ==========
  if (videoRect) {
    renderTubeCropDisplay(irisCtx, videoRect)
    renderTubeTrackerDebug(irisCtx, videoRect)
  }
}

/* -------------------------------------------------------------------------- */
/* MEASUREMENT OVERLAY DRAWING HELPERS                                        */
/* -------------------------------------------------------------------------- */

/**
 * Draw the measurement overlay: static eye-side text and dynamic paper tube circles.
 * Called every frame from drawIrisAndPupil after irises are drawn.
 */
const _drawMeasurementOverlay = (
  videoRect,
  leftPxScreen,
  rightPxScreen,
  ipdScreenPx,
) => {
  if (!irisCtx || !measurementOverlayState) return

  const { isPaperMode, eye, leftTextWords, rightTextWords } =
    measurementOverlayState

  // 1. Draw static eye-side text alongside the video
  _drawEyeSideText(videoRect, leftTextWords, rightTextWords)

  // 2. Draw paper tube circles and (optionally) connecting tangent lines.
  // The tangent-line geometry is always computed (even when not drawn) and
  // fed to the tube tracker, which watches inside the guide for the paper.
  if (isPaperMode && leftPxScreen && rightPxScreen && ipdScreenPx > 0) {
    const smallCircle = _drawTubeCircles(
      leftPxScreen,
      rightPxScreen,
      ipdScreenPx,
      eye,
    )
    _drawTubeLines(
      smallCircle,
      videoRect,
      trackingOptions.calibrateDistanceDrawPaperTubeBool,
    )
  }
}

/**
 * Draw eye-side instruction text just OUTSIDE the video, one word per line.
 * Left text sits to the left of the video, right-aligned so it hugs the
 * video's left edge. Right text sits to the right of the video, left-aligned
 * so it hugs the video's right edge. Both are vertically centered on the
 * video. Keeping the text off the video keeps it legible regardless of what
 * the camera sees.
 */
const _drawEyeSideText = (videoRect, leftTextWords, rightTextWords) => {
  if (!irisCtx) return

  // Scale font size relative to video height for readability
  const fontSize = Math.max(14, Math.min(28, videoRect.height * 0.06))
  const lineHeight = fontSize * 1.3
  const gap = Math.max(6, videoRect.width * 0.015)

  irisCtx.font = `bold ${fontSize}px Arial, sans-serif`
  irisCtx.fillStyle = 'black'

  const widestWord = words =>
    words.reduce((max, w) => Math.max(max, irisCtx.measureText(w).width), 0)

  const topOf = words =>
    videoRect.top +
    (videoRect.height - words.length * lineHeight) / 2 +
    fontSize * 0.85

  // Left of the video, right-aligned against the video's left edge
  if (leftTextWords && leftTextWords.length > 0) {
    const startY = topOf(leftTextWords)
    // Keep the longest word on screen if the video sits near the left edge
    const x = Math.max(widestWord(leftTextWords), videoRect.left - gap)
    irisCtx.textAlign = 'right'
    leftTextWords.forEach((word, i) => {
      irisCtx.fillText(word, x, startY + i * lineHeight)
    })
  }

  // Right of the video, left-aligned against the video's right edge
  if (rightTextWords && rightTextWords.length > 0) {
    const startY = topOf(rightTextWords)
    const canvasW = irisCanvas ? irisCanvas.width : window.innerWidth
    // Keep the longest word on screen if the video sits near the right edge
    const x = Math.min(
      videoRect.right + gap,
      canvasW - widestWord(rightTextWords),
    )
    irisCtx.textAlign = 'left'
    rightTextWords.forEach((word, i) => {
      irisCtx.fillText(word, x, startY + i * lineHeight)
    })
  }

  // Reset text align
  irisCtx.textAlign = 'left'
}

/**
 * Draw paper tube circle on the video frame.
 *
 * Geometry (from the spec):
 *   - Paper tube radius  = 0.27 × ipdVpx
 *
 * Eye side is determined by the participant's hand preference
 * (preferRightHandBool → 'right', else 'left').
 * The obsolete "unspecified" case with two dashed circles has been removed.
 *
 * For 'left':  one solid black circle LEFT of the left eye
 * For 'right': one solid black circle RIGHT of the right eye
 */
const _drawTubeCircles = (leftPx, rightPx, ipdScreenPx, eye) => {
  if (!irisCtx) return null

  const tubeCm = trackingOptions.calibrateDistanceTubeDiameterCm ?? 3.5
  const ipdCm = RC_instance?._CONST?.IPD_CM ?? 6.3
  const tubeRadiusPx = (tubeCm / (2 * ipdCm)) * ipdScreenPx

  irisCtx.strokeStyle = 'black'
  irisCtx.lineWidth = 1
  irisCtx.setLineDash([])

  let cx, cy
  if (eye === 'left') {
    // Solid circle left of the left eye (extending away from the right eye)
    cx = leftPx.x //+ nx * tubeOffsetPx
    cy = leftPx.y //+ ny * tubeOffsetPx
  } else {
    // Solid circle right of the right eye (extending away from the left eye)
    // Default to 'right' for any non-'left' value
    cx = rightPx.x //- nx * tubeOffsetPx
    cy = rightPx.y //- ny * tubeOffsetPx
  }

  irisCtx.beginPath()
  irisCtx.arc(cx, cy, tubeRadiusPx, 0, 2 * Math.PI)
  irisCtx.stroke()

  return { cx, cy, r: tubeRadiusPx }
}

/**
 * Draw two straight lines connecting the small tube circle (on the video) to
 * the big tube circle (below the video), each tangent to both circles.
 *
 * The two external-tangent lines suggest the outline of a cylindrical paper
 * tube: one line runs along the left side, the other along the right side.
 *
 * Geometry: given small circle (cx, cy, r) and big circle (Cx, Cy, R),
 * the outward-pointing normal angle α for an external tangent satisfies
 *   cos(α − θ) = (r − R) / d
 * where θ = atan2(Cy−cy, Cx−cx) and d = dist(centers).
 * Each α yields a tangent point on each circle at (center + radius·(cosα, sinα)).
 *
 * The quadrilateral bounded by the two tangent lines, from the small circle
 * down to the bottom edge of the video, is where the participant holds the
 * tube. In LEGACY crop modes it is passed to the tube tracker (setTubeGuideQuad)
 * every frame. The geometry is computed even when drawing is disabled.
 */
const _drawTubeLines = (smallCircle, videoRect, drawBool = true) => {
  if (!irisCtx || !smallCircle) return

  const bigEl = document.getElementById('rc-big-circle-target')
  if (!bigEl || bigEl.style.display === 'none') return

  const bigRect = bigEl.getBoundingClientRect()
  const Cx = bigRect.left + bigRect.width / 2
  const Cy = bigRect.top + bigRect.height / 2
  const R = bigRect.width / 2

  const { cx, cy, r } = smallCircle

  const dxC = Cx - cx
  const dyC = Cy - cy
  const d = Math.hypot(dxC, dyC)
  if (d <= Math.abs(R - r) || d === 0) return

  const theta = Math.atan2(dyC, dxC)
  const cosVal = (r - R) / d
  if (Math.abs(cosVal) > 1) return

  const phi = Math.acos(cosVal)

  const alpha1 = theta + phi
  const alpha2 = theta - phi

  // Tangent points on small circle
  const p1x = cx + r * Math.cos(alpha1)
  const p1y = cy + r * Math.sin(alpha1)
  const p2x = cx + r * Math.cos(alpha2)
  const p2y = cy + r * Math.sin(alpha2)

  // Tangent points on big circle
  const P1x = Cx + R * Math.cos(alpha1)
  const P1y = Cy + R * Math.sin(alpha1)
  const P2x = Cx + R * Math.cos(alpha2)
  const P2y = Cy + R * Math.sin(alpha2)

  // Feed the tracker the guide region ON THE VIDEO: tangent points at the
  // small circle down to where each tangent line crosses the video's bottom
  // edge (the big circle sits below the video). Ordered as a simple polygon.
  if (videoRect && P1y > p1y && P2y > p2y) {
    const bottomY = videoRect.bottom
    const t1 = (bottomY - p1y) / (P1y - p1y)
    const t2 = (bottomY - p2y) / (P2y - p2y)
    if (t1 > 0 && t2 > 0) {
      const b1x = p1x + Math.min(t1, 1) * (P1x - p1x)
      const b1y = p1y + Math.min(t1, 1) * (P1y - p1y)
      const b2x = p2x + Math.min(t2, 1) * (P2x - p2x)
      const b2y = p2y + Math.min(t2, 1) * (P2y - p2y)
      setTubeGuideQuad(
        [
          { x: p1x, y: p1y },
          { x: b1x, y: b1y },
          { x: b2x, y: b2y },
          { x: p2x, y: p2y },
        ],
        videoRect,
      )
    }
  }

  if (!drawBool) return

  irisCtx.strokeStyle = 'black'
  irisCtx.lineWidth = 0.9
  irisCtx.setLineDash([])

  irisCtx.beginPath()
  irisCtx.moveTo(p1x, p1y)
  irisCtx.lineTo(P1x, P1y)
  irisCtx.stroke()

  irisCtx.beginPath()
  irisCtx.moveTo(p2x, p2y)
  irisCtx.lineTo(P2x, P2y)
  irisCtx.stroke()
}

const startIrisDrawing = RC => {
  RC_instance = RC // Store RC instance for independent access

  // Only create canvas and start drawing if iris drawing is enabled
  if (!trackingOptions.showIrisesBool) {
    console.log('Iris drawing disabled - showIrisesBool is false')
    return
  }

  if (!irisCanvas) createIrisCanvas()

  // Start independent iris drawing loop that uses shared mesh data
  const renderIris = currentTime => {
    if (iRepeatOptions.break) return

    // Throttle to 30fps
    if (currentTime - lastIrisFrameTime >= frameInterval) {
      // Use the mesh data from the shared face data (updated by distance tracking)
      if (sharedFaceData) {
        drawIrisAndPupil()

        if (
          objectLengthCmGlobal.value !== null &&
          trackingOptions.showNearestPointsBool
        ) {
          const {
            leftEye,
            rightEye,
            video,
            currentIPDDistance,
            ipdShrinkage: overlayIpdShrinkage,
            yawDeg: overlayYawDeg,
          } = sharedFaceData
          const correctedIPD = correctIpdForHeadRotation(
            currentIPDDistance,
            overlayIpdShrinkage,
          )
          const eyeToPointCm = objectLengthCmGlobal.value
          const ppi = RC.screenPpi
            ? RC.screenPpi.value
            : RC._CONST.N.PPI_DONT_USE
          const pxPerCm = ppi / 2.54
          // Use globalPointXYPx for overlay so page 3 = camera/top, page 4 = center
          const overlayPointXYPx =
            globalPointXYPx.value !== null
              ? globalPointXYPx.value
              : getCameraXYPx(RC)
          const nearestPointsData = calculateNearestPoints(
            video,
            leftEye,
            rightEye,
            correctedIPD,
            eyeToPointCm,
            pxPerCm,
            ppi,
            RC,
            trackingOptions,
            0,
            0,
            'object',
            1,
            [],
            [],
            0,
            0,
            correctedIPD,
            false,
            'camera',
            overlayPointXYPx,
          )

          const footToPointCm = nearestPointsData.footToPointCm
          const footToCameraCm = nearestPointsData.footToCameraCm
          const eyeToFootCm = Math.sqrt(eyeToPointCm ** 2 - footToPointCm ** 2)
          const eyeToScreenCm = eyeToFootCm // parallel to optical axis (screen normal)
          const eyeToCameraCm = Math.hypot(eyeToScreenCm, footToCameraCm)

          let factorVpxCm = correctedIPD * eyeToScreenCm
          if (
            trackingOptions.useObjectTestData === 'justCreditCard' ||
            trackingOptions.useObjectTestData === 'autoCreditCard'
          ) {
            try {
              factorVpxCm = RC.fRatio * RC.getHorizontalVpx() * RC._CONST.IPD_CM
            } catch (error) {
              console.error('Error calculating factorVpxCm:', error)
            }
          }

          const rulerBasedEyesToPointCm = objectLengthCmGlobal.value
          const rulerBasedEyesToFootCm = Math.sqrt(
            rulerBasedEyesToPointCm ** 2 - footToPointCm ** 2,
          )
          const cameraResolutionXYVpx = getCameraResolutionXY(RC)
          const horizontalVpx = cameraResolutionXYVpx[0]
          const ipdOverWidth = correctedIPD / horizontalVpx
          const fOverWidth =
            (rulerBasedEyesToFootCm * ipdOverWidth) / RC._CONST.IPD_CM
          const imageBasedEyesToFootCm =
            (fOverWidth * RC._CONST.IPD_CM) / ipdOverWidth
          const imageBasedEyesToPointCm = Math.sqrt(
            imageBasedEyesToFootCm ** 2 + footToPointCm ** 2,
          )
          _drawNearestPoints(
            nearestPointsData.clampedNearestLeft,
            nearestPointsData.clampedNearestRight,
            nearestPointsData.nearestDistanceCm_left,
            nearestPointsData.nearestDistanceCm_right,
            trackingOptions.decimalPlace,
            nearestPointsData.nearestEyeToWebcamDistanceCM,
            factorVpxCm,
            correctedIPD,
            {
              x: leftEye.x,
              y: leftEye.y,
            },
            {
              x: rightEye.x,
              y: rightEye.y,
            },
            nearestPointsData.cameraXYPx,
            trackingOptions.viewingDistanceWhichEye,
            trackingOptions.viewingDistanceWhichPoint,
            nearestPointsData.nearestXYPx,
            nearestPointsData.distanceCm_left,
            nearestPointsData.distanceCm_right,
            nearestPointsData.pointXYPx,
            imageBasedEyesToPointCm,
            imageBasedEyesToFootCm,
            rulerBasedEyesToPointCm,
            rulerBasedEyesToFootCm,
            ipdOverWidth,
            fOverWidth,
            getCameraResolutionXY(RC),
            [window.screen.width, window.screen.height],
            pxPerCm,
            nearestPointsData.footXYPx,
            RC._CONST.IPD_CM,
            overlayYawDeg,
            overlayIpdShrinkage,
          )
        }
      }
      lastIrisFrameTime = currentTime
    }

    irisRafId = requestAnimationFrame(renderIris)
  }

  irisRafId = requestAnimationFrame(renderIris)
}

const stopIrisDrawing = () => {
  // Cancel animation frame
  if (irisRafId) {
    cancelAnimationFrame(irisRafId)
    irisRafId = null
  }

  // Clear canvas
  if (irisCtx) {
    irisCtx.clearRect(0, 0, irisCanvas.width, irisCanvas.height)
  }

  RC_instance = null // Clear the RC instance reference
  sharedFaceData = null // Clear shared data
}

const updateSharedFaceData = (
  leftEye,
  rightEye,
  video,
  currentIPDDistance,
  ipdShrinkage = 1,
  yawDeg = 0,
) => {
  sharedFaceData = {
    leftEye,
    rightEye,
    video,
    currentIPDDistance,
    ipdShrinkage,
    yawDeg,
  }
}

// Start iris drawing with continuous mesh data tracking
export const startIrisDrawingWithMesh = async RC => {
  console.log('=== Starting iris drawing with continuous mesh tracking ===')

  // Check if iris drawing is enabled
  if (!trackingOptions.showIrisesBool) {
    console.log('Iris drawing disabled - showIrisesBool is false')
    return false
  }

  // Start the iris drawing canvas first
  startIrisDrawing(RC)

  // Create a continuous tracking loop for iris drawing during calibration
  let irisTrackingActive = true
  let irisTrackingRafId = null

  const trackIrisPosition = async currentTime => {
    if (!irisTrackingActive || iRepeatOptions.break) {
      console.log('Iris tracking stopped')
      return
    }

    // Throttle to 30fps for drawing, but we use a freshness window to avoid flicker
    if (currentTime - lastIrisTrackingTime >= frameInterval) {
      // Get current mesh data
      const meshData = await getMeshData(
        RC,
        trackingOptions.calibrateDistancePupil,
      )
      if (meshData && meshData.leftEye && meshData.rightEye) {
        // Update last time we saw a valid mesh
        lastIrisValidTime = currentTime
        const { leftEye, rightEye, video, currentIPDDistance } = meshData

        // Update shared face data for iris drawing
        updateSharedFaceData(
          leftEye,
          rightEye,
          video,
          currentIPDDistance,
          meshData.ipdShrinkage,
          meshData.yawDeg,
        )

        // Keep latest yaw on RC so the head-rotation nudger can read it
        // during calibration (before the main _tracking loop starts).
        if (typeof meshData.yawDeg === 'number' && !isNaN(meshData.yawDeg)) {
          RC._distanceTrackNudging.headYawDeg = meshData.yawDeg
        }

        // Show the "face the screen" nudger during calibration when the
        // participant's head is rotated past the allowed threshold.
        if (
          RC._distanceTrackNudging.headRotationCorrectingEnabled &&
          RC._distanceTrackNudging.headRotationAllowedDeg < 180 &&
          typeof RC.nudgeHeadRotation === 'function'
        ) {
          RC.nudgeHeadRotation(false, null)
        }

        // Log occasionally for debugging (every ~30 frames at 30fps = 1 second)
        // if (Math.random() < 0.033) {
        //   console.log('Iris tracking update:', {
        //     leftEye: `(${leftEye.x.toFixed(1)}, ${leftEye.y.toFixed(1)})`,
        //     rightEye: `(${rightEye.x.toFixed(1)}, ${rightEye.y.toFixed(1)})`,
        //     ipd: currentIPDDistance.toFixed(1),
        //   })
        // }
      }
      // Compute active status based on freshness window (no motion required)
      const rawIrisTrackingActive =
        currentTime - lastIrisValidTime <= IRIS_VALIDITY_WINDOW_MS
      updateTubeTracker(meshData, rawIrisTrackingActive)
      irisTrackingIsActive = rawIrisTrackingActive && !tubeTrackerForcesRed()
      lastIrisTrackingTime = currentTime
    }

    // Continue tracking
    irisTrackingRafId = requestAnimationFrame(trackIrisPosition)
  }

  // Start the tracking loop
  irisTrackingRafId = requestAnimationFrame(trackIrisPosition)

  // Store cleanup function on RC instance so it can be called later
  RC._stopIrisTracking = () => {
    console.log('=== Stopping iris tracking ===')
    irisTrackingActive = false
    if (irisTrackingRafId) {
      cancelAnimationFrame(irisTrackingRafId)
      irisTrackingRafId = null
    }
  }

  console.log(
    'Iris tracking started - irises will follow face movement during calibration',
  )
  return true
}

// Factor out mesh data retrieval for reuse
// Always uses fresh estimateFaces() for consistency with checkDistance
export const getMeshData = async (
  RC,
  calibrateDistancePupil = 'iris',
  meshSamples = [],
) => {
  const video = getFaceMeshVideoInput()
  if (!video) {
    console.log('Video canvas not ready for mesh data retrieval')
    return null
  }

  let mesh = null

  // Use provided meshSamples if available, otherwise get fresh face detection
  if (meshSamples.length) {
    mesh = meshSamples
  } else {
    // Always use fresh estimateFaces() for accurate, consistent measurements
    try {
      const model = await RC.gazeTracker.webgazer.getTracker().model
      const faces = await model.estimateFaces(video)
      if (faces.length) {
        mesh = faces[0].keypoints
      }
    } catch (error) {
      console.warn('Face detection failed:', error)
      mesh = null
    }
  }

  if (mesh && mesh.length) {
    const { leftEye, rightEye } = getLeftAndRightEyePointsFromMeshData(
      mesh,
      calibrateDistancePupil,
    )
    if (leftEye && rightEye) {
      const useZ = trackingOptions.calibrateDistanceIpdUsesZBool !== false
      const calibrateDistanceCorrectForHeadRotation =
        trackingOptions.calibrateDistanceCorrectForHeadRotation
      const currentIPDDistance = eyeDist(leftEye, rightEye, useZ)
      // Always calculate 3D IPD for ipdOverWidthXYZ
      const ipdXYZVpx = eyeDist(leftEye, rightEye, true)

      const { yawDeg, ipdShrinkage } = estimateHeadYawRobust(
        mesh,
        leftEye,
        rightEye,
        calibrateDistanceCorrectForHeadRotation,
      )

      return {
        mesh,
        leftEye,
        rightEye,
        video,
        currentIPDDistance,
        ipdXYZVpx, // Always 3D IPD
        yawDeg,
        ipdShrinkage,
      }
    }
  }

  return null
}

/* -------------------------------------------------------------------------- */

let readyToGetFirstData = false
let rafId = null

const _tracking = async (
  RC,
  trackingOptions,
  callbackTrack,
  trackingConfig,
) => {
  // const video = document.getElementById('webgazerVideoCanvas')
  RC.improvedDistanceTrackingData = {
    left: {
      nearestXYPx: [0, 0],
      nearestDistanceCm: 0,
      distanceCm: 0,
    },
    right: {
      nearestXYPx: [0, 0],
      nearestDistanceCm: 0,
      distanceCm: 0,
    },
    nearEye: 'left',
    distanceCm: 0,
    nearestXYPx: [0, 0],
    nearestDistanceCm: 0,
    oldDistanceCm: 0,
  }

  const model = await RC.gazeTracker.webgazer.getTracker().model

  // Near point setup
  const ppi = RC.screenPpi ? RC.screenPpi.value : RC._CONST.N.PPI_DONT_USE
  const pxPerCm = ppi / 2.54
  if (!RC.screenPpi && trackingOptions.nearPoint)
    console.error(
      'Screen size measurement is required to get accurate near point tracking.',
    )

  if (trackingOptions.nearPoint && trackingOptions.showNearPoint) {
    nearPointDot = document.createElement('div')
    nearPointDot.id = 'rc-near-point-dot'
    document.body.appendChild(nearPointDot)

    Object.assign(nearPointDot.style, {
      display: 'block',
      zIndex: 999999,
      width: '10px', // TODO Make it customizable
      height: '10px',
      background: 'green',
      position: 'fixed',
      top: '-15px',
      left: '-15px',
    })
  }

  readyToGetFirstData = false
  const {
    desiredDistanceCm,
    desiredDistanceTolerance,
    desiredDistanceMonitor,
    desiredDistanceMonitorCancelable,
    desiredDistanceMonitorAllowRecalibrate,
  } = trackingOptions

  // Always enable correct on a fresh start
  RC._distanceTrackNudging.distanceCorrectEnabled = true
  RC._distanceTrackNudging.distanceDesired = desiredDistanceCm
  RC._distanceTrackNudging.distanceAllowedRatio = desiredDistanceTolerance

  // Note: head-rotation nudging state (headRotationCorrectingEnabled,
  // headRotationAllowedDeg) is initialized earlier in trackDistance(), so
  // it is active throughout calibration as well.

  // Set break to false to start the tracking loop
  iRepeatOptions.break = false

  // Stop the calibration-phase iris tracking since main tracking will take over
  if (RC._stopIrisTracking) {
    RC._stopIrisTracking()
    RC._stopIrisTracking = null
  }

  // Note: Iris drawing canvas is already created, main tracking will update the shared data

  // Main tracking loop using requestAnimationFrame like FaceMesh demo
  const renderPrediction = async currentTime => {
    if (iRepeatOptions.break) {
      // Stop the animation loop if break is set
      return
    }

    // Throttle to 30fps
    if (currentTime - lastFrameTime >= frameInterval) {
      await renderDistanceResult(
        RC,
        trackingOptions,
        callbackTrack,
        trackingConfig,
        model,
        ppi,
        pxPerCm,
        desiredDistanceCm,
        desiredDistanceMonitor,
        desiredDistanceMonitorCancelable,
        desiredDistanceMonitorAllowRecalibrate,
      )
      lastFrameTime = currentTime
    }

    rafId = requestAnimationFrame(renderPrediction)
  }

  // Start the tracking loop
  rafId = requestAnimationFrame(renderPrediction)
}

export const calculateFootXYPx = (
  RC,
  video,
  leftEye,
  rightEye,
  pxPerCm,
  currentIPDDistance,
) => {
  // Use LIVE camera resolution instead of stale canvas dimensions
  const cameraRes = getCameraResolutionXY(RC)
  const camWidth = cameraRes[0] || video.width
  const camHeight = cameraRes[1] || video.height
  const centerXYVpx = [camWidth / 2, camHeight / 2]

  // Mirror correction: Video is horizontally flipped, so flip X coordinates to match screen
  //left eye: 468
  //right eye: 473
  const leftEyeX = camWidth - leftEye.x // Flip X coordinate
  const leftEyeY = leftEye.y // Y coordinate unchanged
  const rightEyeX = camWidth - rightEye.x // Flip X coordinate
  const rightEyeY = rightEye.y // Y coordinate unchanged

  const useZ = trackingOptions.calibrateDistanceIpdUsesZBool !== false
  const ipdVpx = eyeDist(leftEye, rightEye, useZ)

  const offsetXYVpx_left = [
    leftEyeX - centerXYVpx[0],
    leftEyeY - centerXYVpx[1],
  ]
  const offsetXYVpx_right = [
    rightEyeX - centerXYVpx[0],
    rightEyeY - centerXYVpx[1],
  ]

  const offsetXYCm_left = [
    (offsetXYVpx_left[0] * RC._CONST.IPD_CM) / currentIPDDistance,
    (offsetXYVpx_left[1] * RC._CONST.IPD_CM) / currentIPDDistance,
  ]
  const offsetXYCm_right = [
    (offsetXYVpx_right[0] * RC._CONST.IPD_CM) / currentIPDDistance,
    (offsetXYVpx_right[1] * RC._CONST.IPD_CM) / currentIPDDistance,
  ]

  // Anchor point of the participant's camera in screen CSS px:
  // top-centre for top-camera setups, bottom-centre for bottom-camera
  // setups (driven by RC.selectedCameraRow set on the Choose Camera
  // page when calibrateDistanceAcceptBottomCameraBool is true).
  const cameraXYPx = getCameraXYPx(RC)

  const nearestXYPx_left = [
    cameraXYPx[0] + offsetXYCm_left[0] * pxPerCm,
    cameraXYPx[1] + offsetXYCm_left[1] * pxPerCm,
  ]
  const nearestXYPx_right = [
    cameraXYPx[0] + offsetXYCm_right[0] * pxPerCm,
    cameraXYPx[1] + offsetXYCm_right[1] * pxPerCm,
  ]

  return {
    nearestXYPx_left,
    nearestXYPx_right,
    ipdVpx,
    offsetXYCm_left,
    offsetXYCm_right,
    cameraXYPx,
    pxPerCm,
  }
}

// Function to calculate nearest points for both eyes
export const calculateNearestPoints = (
  video,
  leftEye,
  rightEye,
  currentIPDDistance,
  webcamToEyeDistance, //for distance check: calibration factor / ipdVpx
  pxPerCm,
  ppi,
  RC,
  options = {},
  leftMean = 0,
  rightMean = 0,
  method = 'object',
  order = 1, // 1 for first measurement (right-eye for blindspot, page3 for object), 2 for second measurement (left-eye for blindspot, page4 for object)
  fixPoint = [window.screen.width / 2, window.screen.height / 2],
  spotPoint = [window.screen.width / 2, window.screen.height / 2],
  blindspotDeg = 0,
  fixationToSpotCm = 0,
  ipdVpx = 0,
  distanceCheck = false,
  calibrateDistanceChecking = 'camera',
  _pointXYPx = [window.screen.width / 2, window.screen.height / 2],
  eye = 'unspecified',
) => {
  const {
    nearestXYPx_left,
    nearestXYPx_right,
    offsetXYCm_left,
    offsetXYCm_right,
    cameraXYPx,
  } = calculateFootXYPx(
    RC,
    video,
    leftEye,
    rightEye,
    pxPerCm,
    currentIPDDistance,
  )
  const centerXYPx = [window.screen.width / 2, window.screen.height / 2]
  let pointXYPx = distanceCheck ? cameraXYPx : _pointXYPx
  // Only use globalPointXYPx for live overlay (distanceCheck). When saving calibration
  // (distanceCheck false) use _pointXYPx so page 3 gets camera/top and page 4 gets center.
  // if (distanceCheck && globalPointXYPx.value !== null) {
  //   pointXYPx = globalPointXYPx.value
  // }
  if (
    distanceCheck &&
    calibrateDistanceChecking &&
    typeof calibrateDistanceChecking === 'string'
  ) {
    const optionsArray = calibrateDistanceChecking
      .toLowerCase()
      .split(',')
      .map(s => s.trim())
    if (optionsArray.includes('camera')) pointXYPx = cameraXYPx
    else if (optionsArray.includes('center')) pointXYPx = centerXYPx
  }
  const avgFootXYPx = [
    (nearestXYPx_right[0] + nearestXYPx_left[0]) / 2,
    (nearestXYPx_right[1] + nearestXYPx_left[1]) / 2,
  ]
  const footXYPx =
    distanceCheck || method !== 'blindspot'
      ? eye === 'unspecified'
        ? avgFootXYPx
        : eye === 'left'
          ? nearestXYPx_left
          : nearestXYPx_right
      : order === 1
        ? nearestXYPx_right
        : nearestXYPx_left

  const cameraToCenterCm =
    Math.hypot(centerXYPx[0] - cameraXYPx[0], centerXYPx[1] - cameraXYPx[1]) /
    pxPerCm
  const footToCenterCm =
    Math.hypot(centerXYPx[0] - footXYPx[0], centerXYPx[1] - footXYPx[1]) /
    pxPerCm

  const footToCameraCm =
    Math.hypot(cameraXYPx[0] - footXYPx[0], cameraXYPx[1] - footXYPx[1]) /
    pxPerCm

  const footToPointCm =
    Math.hypot(pointXYPx[0] - footXYPx[0], pointXYPx[1] - footXYPx[1]) / pxPerCm

  let eyeToFootCm = 0
  let eyeToScreenCm = 0 // distance parallel to optical axis (screen normal)
  let eyeToCameraCm = 0
  let eyeToPointCm = 0

  if (webcamToEyeDistance === 0 || method === 'blindspot') {
    //blindspot
    try {
      const { d_cm, d_px } = solveEyeToScreenCm(
        order === 1 ? nearestXYPx_right : nearestXYPx_left,
        fixPoint,
        spotPoint,
        blindspotDeg,
        pxPerCm,
      )
      eyeToFootCm = d_cm
      eyeToScreenCm = eyeToFootCm
      eyeToCameraCm = Math.hypot(footToCameraCm, eyeToScreenCm)
      // TEMP: use _getEyeToCameraCm instead of solveEyeToScreenCm
      // eyeToFootCm = _getEyeToCameraCm(
      //   fixationToSpotCm,
      //   options.calibrateDistanceSpotXYDeg,
      // )
    } catch (e) {
      // eyeToFootCm = _getEyeToCameraCm(
      //   fixationToSpotCm,
      //   options.calibrateDistanceSpotXYDeg,
      // )
      throw new Error(e)
    }
  } else if (distanceCheck) {
    eyeToFootCm = webcamToEyeDistance
    eyeToPointCm = Math.sqrt(eyeToFootCm ** 2 + footToPointCm ** 2)
  } else {
    // eyeToScreenCm = webcamToEyeDistance // = fVpx * ipdCm / ipdVpx
    // eyeToCameraCm = Math.hypot(eyeToScreenCm, footToCameraCm)
    // eyeToFootCm = eyeToScreenCm
    eyeToPointCm = webcamToEyeDistance // objectLengthCm
    eyeToFootCm = Math.sqrt(eyeToPointCm ** 2 - footToPointCm ** 2)
  }

  const eyeToCenterCm = Math.sqrt(
    eyeToFootCm * eyeToFootCm + footToCenterCm * footToCenterCm,
  )
  // const eyeToPointCm = Math.sqrt(
  //   eyeToFootCm * eyeToFootCm + footToPointCm * footToPointCm,
  // )

  let calibrationFactor = Math.round(eyeToFootCm * ipdVpx)
  if (
    trackingOptions.useObjectTestData === 'justCreditCard' ||
    trackingOptions.useObjectTestData === 'autoCreditCard'
  ) {
    try {
      calibrationFactor = Math.round(
        RC.fRatio * RC.getHorizontalVpx() * RC._CONST.IPD_CM,
      )
    } catch (error) {
      console.error('Error calculating calibrationFactor:', error)
    }
  }

  // Clamp coordinates to stay within viewport bounds
  const clampedNearestLeft = [
    Math.max(0, Math.min(nearestXYPx_left[0], window.innerWidth)),
    Math.max(0, Math.min(nearestXYPx_left[1], window.innerHeight)),
  ]
  const clampedNearestRight = [
    Math.max(0, Math.min(nearestXYPx_right[0], window.innerWidth)),
    Math.max(0, Math.min(nearestXYPx_right[1], window.innerHeight)),
  ]

  //calculate nearest distance cm left and right from webcamToEyeDistance and OffsetXYCm_left and OffsetXYCm_right
  const norm_offsetXYCm_left = Math.hypot(
    offsetXYCm_left[0],
    offsetXYCm_left[1],
  )
  const norm_offsetXYCm_right = Math.hypot(
    offsetXYCm_right[0],
    offsetXYCm_right[1],
  )
  const cameraToEyeDistance = webcamToEyeDistance

  const nearestDistanceCm_left = Math.sqrt(
    cameraToEyeDistance ** 2 - norm_offsetXYCm_left ** 2,
  )
  const nearestDistanceCm_right = Math.sqrt(
    cameraToEyeDistance ** 2 - norm_offsetXYCm_right ** 2,
  )

  const eyeToScreenCenterDistance_left = getEyeToDesiredDistance(
    nearestXYPx_left,
    nearestDistanceCm_left,
    [window.screen.width / 2, window.screen.height / 2],
    pxPerCm,
  )
  const eyeToScreenCenterDistance_right = getEyeToDesiredDistance(
    nearestXYPx_right,
    nearestDistanceCm_right,
    [window.screen.width / 2, window.screen.height / 2],
    pxPerCm,
  )
  //choose the nearest eye to screen center distance
  const nearestEye =
    eyeToScreenCenterDistance_left < eyeToScreenCenterDistance_right
      ? 'left'
      : 'right'
  const nearestXYPx =
    nearestEye === 'left' ? nearestXYPx_left : nearestXYPx_right
  const nearestDistanceCm =
    nearestEye === 'left' ? nearestDistanceCm_left : nearestDistanceCm_right
  const distanceCm_left = getEyeToDesiredDistance(
    nearestXYPx_left,
    nearestDistanceCm_left,
    [window.screen.width / 2, window.screen.height / 2],
    pxPerCm,
  )
  const distanceCm_right = getEyeToDesiredDistance(
    nearestXYPx_right,
    nearestDistanceCm_right,
    [window.screen.width / 2, window.screen.height / 2],
    pxPerCm,
  )

  const nearestEyeToWebcamDistanceCM = eyeToFootCm

  const distanceCm = eyeToFootCm

  return {
    nearestXYPx_left,
    nearestXYPx_right,
    clampedNearestLeft,
    clampedNearestRight,
    nearestDistanceCm_left,
    nearestDistanceCm_right,
    nearestEyeToWebcamDistanceCM,
    nearestEye,
    nearestXYPx,
    nearestDistanceCm,
    distanceCm_left,
    distanceCm_right,
    distanceCm,
    ipdVpx,
    cameraXYPx,
    viewingDistanceWhichEye: options?.viewingDistanceWhichEye,
    viewingDistanceWhichPoint: options?.viewingDistanceWhichPoint,
    calibrationFactor,
    eyeToCameraCm,
    cameraToCenterCm,
    footToCameraCm,
    footToCenterCm,
    eyeToCenterCm,
    footXYPx,
    footToPointCm,
    eyeToPointCm,
    pointXYPx,
    eyeToFootCm,
  }
}

const renderDistanceResult = async (
  RC,
  trackingOptions,
  callbackTrack,
  trackingConfig,
  model,
  ppi,
  pxPerCm,
  desiredDistanceCm,
  desiredDistanceMonitor,
  desiredDistanceMonitorCancelable,
  desiredDistanceMonitorAllowRecalibrate,
) => {
  //console.log('././renderDistanceResult')
  if (!video) video = document.getElementById('webgazerVideoCanvas')

  // Check if video is ready
  if (!video) {
    return
  }

  const videoTimestamp = performance.now()

  // Use the factored out mesh data retrieval
  const meshData = await getMeshData(RC, trackingOptions.calibrateDistancePupil)
  ensureVideoPlaying(document.getElementById('webgazerVideoFeed'))

  if (meshData) {
    const { mesh, leftEye, rightEye, currentIPDDistance, ipdShrinkage } =
      meshData
    const correctedIPD = correctIpdForHeadRotation(
      currentIPDDistance,
      ipdShrinkage,
    )
    RC._trackingVideoFrameTimestamps.distance = videoTimestamp

    // Initialize calibration factor on first run
    if (stdDist.current !== null) {
      if (!stdFactor) {
        // ! First time estimate
        // ALWAYS use the pre-calculated calibration factor from measurement tests
        if (
          trackingOptions.useObjectTestData === 'justCreditCard' ||
          trackingOptions.useObjectTestData === 'autoCreditCard'
        ) {
          try {
            stdFactor = RC.fRatio * RC.getHorizontalVpx() * RC._CONST.IPD_CM
          } catch (error) {
            console.error('Error calculating stdFactor:', error)
          }
        } else {
          if (stdDist.current.calibrationFactor) {
            console.log(
              'Using pre-calculated calibration factor:',
              stdDist.current.calibrationFactor,
            )
            console.log('Method used:', stdDist.current.method)
            stdFactor = stdDist.current.calibrationFactor
          } else {
            console.error(
              'No calibration factor found! This should not happen.',
            )
            console.error('Measurement data:', stdDist.current)
            return
          }
        }

        // ! FINISH
        if (trackingConfig.options.calibrateDistanceCheckBool !== true)
          RC._removeBackground() // Remove BG if no check

        RC._trackingSetupFinishedStatus.distance = true
        readyToGetFirstData = true
      }

      /* -------------------------------------------------------------------------- */

      const timestamp = performance.now()
      const latency = Math.round(
        timestamp - RC._trackingVideoFrameTimestamps.distance,
      )

      // const webcamToEyeDistance = stdFactor / currentIPDDistance
      // const VpxPerCm = currentIPDDistance / RC._CONST.IPD_CM

      const cameraResolutionXYVpx = getCameraResolutionXY(RC)
      const horizontalVpx = cameraResolutionXYVpx[0]
      const ipdOverWidth = correctedIPD / horizontalVpx
      const eyesToFootCm =
        (RC.calibrationFOverWidth * RC._CONST.IPD_CM) / ipdOverWidth

      // Calculate nearest points using the factored function
      const nearestPointsData = calculateNearestPoints(
        video,
        leftEye,
        rightEye,
        correctedIPD,
        eyesToFootCm,
        pxPerCm,
        ppi,
        RC,
        trackingOptions,
        0,
        0,
        '',
        1,
        [],
        [],
        0,
        0,
        correctedIPD,
        true,
        trackingOptions.calibrateDistanceChecking,
      )

      const {
        nearestXYPx_left,
        nearestXYPx_right,
        clampedNearestLeft,
        clampedNearestRight,
        nearestDistanceCm_left,
        nearestDistanceCm_right,
        nearestEyeToWebcamDistanceCM,
        nearestEye,
        nearestXYPx,
        nearestDistanceCm,
        distanceCm_left,
        distanceCm_right,
        distanceCm,
        cameraXYPx,
        viewingDistanceWhichEye,
        viewingDistanceWhichPoint,
        footToPointCm,
      } = nearestPointsData

      // Apply trigonometric adjustment to get screen-center-to-eye distance
      // const screenCenterToEyeDistance = _adjustDistanceToScreenCenter(
      //   eyesToFootCm,
      //   ppi,
      // )

      RC.improvedDistanceTrackingData = {
        left: {
          nearestXYPx: finitePairOrNull(nearestXYPx_left),
          nearestDistanceCm: nearestDistanceCm_left,
          distanceCm: distanceCm_left,
        },
        right: {
          nearestXYPx: finitePairOrNull(nearestXYPx_right),
          nearestDistanceCm: nearestDistanceCm_right,
          distanceCm: distanceCm_right,
        },
        nearEye: nearestEye,
        distanceCm: distanceCm,
        nearestXYPx: finitePairOrNull(nearestXYPx),
        nearestDistanceCm: nearestDistanceCm,
        // oldDistanceCm: screenCenterToEyeDistance,
        ipdDistancePx: correctedIPD,
      }

      const data = {
        value: toFixedNumber(distanceCm, trackingOptions.decimalPlace),
        timestamp: timestamp,
        method: RC._CONST.VIEW_METHOD.F,
        latencyMs: latency,
        calibrationMethod: stdDist.method, // Include which method was used
      }

      RC.newViewingDistanceData = data

      // Update shared face data for iris drawing
      updateSharedFaceData(
        leftEye,
        rightEye,
        video,
        currentIPDDistance,
        ipdShrinkage,
        meshData.yawDeg,
      )

      // Debug: Draw nearest points on screen using clamped coordinates
      console.log(
        'nearestPointsData...:',
        nearestPointsData,
        'objectLengthCmGlobal...:',
        objectLengthCmGlobal,
        'correctedIPD...:',
        correctedIPD,
        'ipdShrinkage...:',
        ipdShrinkage,
        'meshData.yawDeg...:',
        meshData.yawDeg,
      )
      if (trackingConfig.options.showNearestPointsBool) {
        const rulerBasedEyesToPointCm = objectLengthCmGlobal.value
        const rulerBasedEyesToFootCm = Math.sqrt(
          rulerBasedEyesToPointCm ** 2 - footToPointCm ** 2,
        )
        const cameraResolutionXYVpx = getCameraResolutionXY(RC)
        const horizontalVpx = cameraResolutionXYVpx[0]
        const ipdOverWidth = correctedIPD / horizontalVpx
        const fOverWidth =
          (rulerBasedEyesToFootCm * ipdOverWidth) / RC._CONST.IPD_CM
        const imageBasedEyesToFootCm =
          (fOverWidth * RC._CONST.IPD_CM) / ipdOverWidth
        const imageBasedEyesToPointCm = Math.sqrt(
          imageBasedEyesToFootCm ** 2 + footToPointCm ** 2,
        )

        _drawNearestPoints(
          clampedNearestLeft,
          clampedNearestRight,
          nearestDistanceCm_left,
          nearestDistanceCm_right,
          trackingOptions.decimalPlace,
          nearestEyeToWebcamDistanceCM,
          stdFactor,
          correctedIPD,
          {
            x: leftEye.x,
            y: leftEye.y,
          },
          {
            x: rightEye.x,
            y: rightEye.y,
          },
          cameraXYPx,
          viewingDistanceWhichEye,
          viewingDistanceWhichPoint,
          nearestXYPx,
          distanceCm_left,
          distanceCm_right,
          nearestPointsData.pointXYPx, // pointXYPx
          imageBasedEyesToPointCm, // imageBasedEyesToPointCm
          imageBasedEyesToFootCm, // imageBasedEyesToFootCm
          rulerBasedEyesToPointCm, // rulerBasedEyesToPointCm
          rulerBasedEyesToFootCm, // rulerBasedEyesToFootCm
          ipdOverWidth, // ipdOverWidth
          fOverWidth, // fOverWidth
          getCameraResolutionXY(RC),
          [window.screen.width, window.screen.height], // screenResolutionXYVpx
          pxPerCm, // pxPerCm
          nearestPointsData.footXYPx, // footXYPx
          RC._CONST.IPD_CM, // ipdCm
          meshData.yawDeg,
          ipdShrinkage,
        )
      }

      // Keep latest head yaw on RC so the head-rotation nudger can read it.
      if (typeof meshData.yawDeg === 'number' && !isNaN(meshData.yawDeg)) {
        RC._distanceTrackNudging.headYawDeg = meshData.yawDeg
      }

      if (readyToGetFirstData || desiredDistanceMonitor) {
        // ! Check distance
        if (desiredDistanceCm) {
          RC.nudgeDistance(
            desiredDistanceMonitorCancelable,
            desiredDistanceMonitorAllowRecalibrate,
            trackingConfig,
          )
        }
        readyToGetFirstData = false
      }

      // ! Check head rotation (yaw). Runs on every rendered frame once
      // tracking is live, independent of desiredDistanceCm. The nudger only
      // shows when yaw estimation is enabled and the allowed-rotation
      // threshold is below 180 (the "ignore" value).
      if (
        RC._distanceTrackNudging.headRotationCorrectingEnabled &&
        RC._distanceTrackNudging.headRotationAllowedDeg < 180
      ) {
        RC.nudgeHeadRotation(desiredDistanceMonitorCancelable, trackingConfig)
      }

      /* -------------------------------------------------------------------------- */

      /* -------------------------------------------------------------------------- */

      if (callbackTrack && typeof callbackTrack === 'function') {
        RC.gazeTracker.defaultDistanceTrackCallback = callbackTrack
        callbackTrack(data)
      }
    }
  } else {
    cleanUpEyePoints()
  }
}

const getEyeToDesiredDistance = (
  nearestXYPx,
  nearestDistanceCm,
  desiredXYPx,
  pxPerCm,
) => {
  const desiredOffsetCm =
    Math.hypot(
      nearestXYPx[0] - desiredXYPx[0],
      nearestXYPx[1] - desiredXYPx[1],
    ) / pxPerCm
  return Math.sqrt(nearestDistanceCm ** 2 + desiredOffsetCm ** 2)
}

const getCenterXYVpx = video => {
  if (!video) return [0, 0]
  const videoWidth = video.width
  const videoHeight = video.height
  const centerX = videoWidth / 2
  const centerY = videoHeight / 2
  return [centerX, centerY]
}

// Helper function to calculate label position avoiding video overlap
const _calculateLabelPosition = (dotX, dotY, eyeSide) => {
  const videoContainer = document.getElementById('webgazerVideoContainer')
  let labelLeft = dotX + 15
  let labelTop = dotY - 12

  // if (videoContainer) {
  //   const videoRect = videoContainer.getBoundingClientRect()
  //   const labelWidth = 80
  //   const labelHeight = 25

  //   const labelRight = labelLeft + labelWidth
  //   const labelBottom = labelTop + labelHeight

  //   const overlapsHorizontally =
  //     labelLeft < videoRect.right && labelRight > videoRect.left
  //   const overlapsVertically =
  //     labelTop < videoRect.bottom && labelBottom > videoRect.top

  //   if (overlapsHorizontally && overlapsVertically) {
  //     if (eyeSide === 'left') {
  //       labelLeft = videoRect.left - labelWidth - 10

  //       if (labelLeft < 0) {
  //         labelLeft = 10
  //       }
  //     } else {
  //       labelLeft = videoRect.right + 10

  //       if (labelLeft + labelWidth > window.innerWidth) {
  //         labelLeft = window.innerWidth - labelWidth - 10
  //       }
  //     }
  //   }
  // }

  labelLeft = Math.max(10, Math.min(labelLeft, window.innerWidth - 90))
  labelTop = Math.max(0, Math.min(labelTop, window.innerHeight - 25))

  return { left: labelLeft, top: labelTop }
}

// Debug function to draw nearest points on screen
let nearestPointDots = { left: null, right: null }
let nearestPointLabels = { left: null, right: null }
let cameraResolutionXYLabel = null
let screenResolutionXYVpxLabel = null
let pxPerCmLabel = null
let ipdCmLabel = null
let ipdLabel = null
let fOverWidthLabel = null
let imageBasedEyesToFootCmLabel = null
let imageBasedEyesToPointCmLabel = null
let rulerBasedEyesToFootCmLabel = null
let rulerBasedEyesToPointCmLabel = null
let footToPointCmLabel = null
let footXYPxLabel = null
let pointXYPxLabel = null
let viewingDistanceWhichEyeLabel = null
let viewingDistanceWhichPointLabel = null
let headYawLabel = null
let headPitchLabel = null
let ipdShrinkageLabel = null
let headRotationDot = null
let eyePointDots = { left: null, right: null }
let pupilDots = { left: null, right: null }
let nearestPointCoordsLabels = { left: null, right: null }

// Throttle for label updates - prevents too-rapid updates making labels unreadable
const LABEL_UPDATE_INTERVAL_MS = 150
let lastLabelUpdateTime = 0
const _drawNearestPoints = (
  nearestLeft,
  nearestRight,
  distanceLeft,
  distanceRight,
  decimalPlace,
  nearestEyeToWebcamDistanceCM,
  factorVpxCm,
  averageDist,
  leftEyePoint,
  rightEyePoint,
  cameraXYPx,
  viewingDistanceWhichEye,
  viewingDistanceWhichPoint,
  nearestXYPx,
  distanceCm_left,
  distanceCm_right,
  pointXYPx = null,
  imageBasedEyesToPointCm = null,
  imageBasedEyesToFootCm = null,
  rulerBasedEyesToPointCm = null,
  rulerBasedEyesToFootCm = null,
  ipdOverWidth = null,
  fOverWidth = null,
  cameraResolutionXY = null,
  screenResolutionXYVpx = null,
  pxPerCm = null,
  footXYPx = null,
  ipdCm = 6.3,
  headYawDeg = null,
  headIpdShrinkage = null,
) => {
  // Get video container and its bounding rect once for reuse
  const videoContainer = document.getElementById('webgazerVideoContainer')
  const videoRect = videoContainer
    ? videoContainer.getBoundingClientRect()
    : null

  // Create elements only if they don't exist, otherwise reuse them
  const createOrUpdateElement = (elementRef, id, baseStyles) => {
    if (!elementRef) {
      elementRef = document.createElement('div')
      elementRef.id = id
      document.body.appendChild(elementRef)
    }
    // Apply base styles that don't change frequently
    if (baseStyles && !elementRef.dataset.baseStylesApplied) {
      Object.assign(elementRef.style, baseStyles)
      elementRef.dataset.baseStylesApplied = 'true'
    }
    return elementRef
  }

  if (
    nearestLeft &&
    nearestLeft[0] !== undefined &&
    nearestLeft[1] !== undefined
  ) {
    // Create or reuse left dot
    nearestPointDots.left = createOrUpdateElement(
      nearestPointDots.left,
      'rc-nearest-point-left',
      {
        position: 'fixed',
        width: '12px',
        height: '12px',
        background: 'red',
        border: '2px solid white',
        borderRadius: '50%',
        zIndex: '99999999999999',
        pointerEvents: 'none',
      },
    )

    // Update only position (frequently changing properties)
    nearestPointDots.left.style.left = `${nearestLeft[0] - 6}px`
    nearestPointDots.left.style.top = `${nearestLeft[1] - 6}px`

    if (distanceLeft !== undefined) {
      // Create or reuse left label
      nearestPointLabels.left = createOrUpdateElement(
        nearestPointLabels.left,
        'rc-nearest-point-label-left',
        {
          position: 'fixed',
          fontSize: '18px',
          color: 'red',
          background: 'rgba(255, 255, 255, 0.8)',
          padding: '2px 6px',
          borderRadius: '4px',
          zIndex: '9999999999999',
          pointerEvents: 'none',
          fontFamily: 'Arial, sans-serif',
          fontWeight: 'normal',
        },
      )

      // Update content and position
      nearestPointLabels.left.textContent = `${distanceLeft.toFixed(decimalPlace || 1)} cm`
      const labelPosition = _calculateLabelPosition(
        nearestLeft[0],
        nearestLeft[1],
        'left',
      )
      nearestPointLabels.left.style.left = `${labelPosition.left}px`
      nearestPointLabels.left.style.top = `${labelPosition.top}px`
    }

    // Create or reuse left coordinates label (positioned right below the dot)
    nearestPointCoordsLabels.left = createOrUpdateElement(
      nearestPointCoordsLabels.left,
      'rc-nearest-point-coords-left',
      {
        position: 'fixed',
        fontSize: '12px',
        color: '#111',
        background: 'rgba(255, 255, 255, 0.9)',
        padding: '1px 4px',
        borderRadius: '3px',
        zIndex: '9999999999999',
        pointerEvents: 'none',
        fontFamily: 'Arial, sans-serif',
        transform: 'translate(-50%, 0)',
      },
    )

    // Update content and position centered below the dot
    nearestPointCoordsLabels.left.textContent = `${Math.round(nearestLeft[0])}, ${Math.round(nearestLeft[1])}`
    nearestPointCoordsLabels.left.style.left = `${nearestLeft[0]}px`
    nearestPointCoordsLabels.left.style.top = `${nearestLeft[1] + 15}px`
  }

  if (
    nearestRight &&
    nearestRight[0] !== undefined &&
    nearestRight[1] !== undefined
  ) {
    // Create or reuse right dot
    nearestPointDots.right = createOrUpdateElement(
      nearestPointDots.right,
      'rc-nearest-point-right',
      {
        position: 'fixed',
        width: '12px',
        height: '12px',
        background: 'blue',
        border: '2px solid white',
        borderRadius: '50%',
        zIndex: '99999999999999',
        pointerEvents: 'none',
      },
    )

    // Update only position
    nearestPointDots.right.style.left = `${nearestRight[0] - 6}px`
    nearestPointDots.right.style.top = `${nearestRight[1] - 6}px`

    if (distanceRight !== undefined) {
      // Create or reuse right label
      nearestPointLabels.right = createOrUpdateElement(
        nearestPointLabels.right,
        'rc-nearest-point-label-right',
        {
          position: 'fixed',
          fontSize: '18px',
          color: 'blue',
          background: 'rgba(255, 255, 255, 0.8)',
          padding: '2px 6px',
          borderRadius: '4px',
          zIndex: '9999999999999',
          pointerEvents: 'none',
          fontFamily: 'Arial, sans-serif',
          fontWeight: 'normal',
        },
      )

      // Update content and position
      nearestPointLabels.right.textContent = `${distanceRight.toFixed(decimalPlace || 1)} cm`
      const labelPosition = _calculateLabelPosition(
        nearestRight[0],
        nearestRight[1],
        'right',
      )
      nearestPointLabels.right.style.left = `${labelPosition.left}px`
      nearestPointLabels.right.style.top = `${labelPosition.top}px`
    }

    // Create or reuse right coordinates label (positioned right below the dot)
    nearestPointCoordsLabels.right = createOrUpdateElement(
      nearestPointCoordsLabels.right,
      'rc-nearest-point-coords-right',
      {
        position: 'fixed',
        fontSize: '12px',
        color: '#111',
        background: 'rgba(255, 255, 255, 0.9)',
        padding: '1px 4px',
        borderRadius: '3px',
        zIndex: '9999999999999',
        pointerEvents: 'none',
        fontFamily: 'Arial, sans-serif',
        transform: 'translate(-50%, 0)',
      },
    )

    // Update content and position centered below the dot
    nearestPointCoordsLabels.right.textContent = `${Math.round(nearestRight[0])}, ${Math.round(nearestRight[1])}`
    nearestPointCoordsLabels.right.style.left = `${nearestRight[0]}px`
    nearestPointCoordsLabels.right.style.top = `${nearestRight[1] + 15}px`
  }

  // NOTE: Iris and pupil drawing is now handled by the separate canvas-based function

  // Helper function to calculate label position
  const calculateLabelLeft = () => {
    let labelLeft = window.innerWidth / 2
    if (videoRect) {
      const labelWidth = 80
      labelLeft = Math.max(window.innerWidth / 2, videoRect.right + 20)
      if (labelLeft + labelWidth > window.innerWidth) {
        labelLeft = Math.max(20, videoRect.left - labelWidth - 20)
      }
    }
    return Math.max(20, Math.min(labelLeft, window.innerWidth - 100))
  }

  const labelBaseStyles = {
    position: 'fixed',
    fontSize: '16px',
    color: '#333',
    background: 'rgba(255, 255, 255, 0.9)',
    padding: '4px 8px',
    borderRadius: '6px',
    border: '1px solid #ddd',
    zIndex: '2147483646',
    pointerEvents: 'none',
    fontFamily: 'Arial, sans-serif',
    fontWeight: 'normal',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  }

  const labelLeft = calculateLabelLeft()

  // 1. cameraResolutionXYVpx label (top 20px)
  if (
    cameraResolutionXY !== null &&
    Array.isArray(cameraResolutionXY) &&
    cameraResolutionXY[0] &&
    cameraResolutionXY[1]
  ) {
    cameraResolutionXYLabel = createOrUpdateElement(
      cameraResolutionXYLabel,
      'rc-camera-resolution-xy-label',
      labelBaseStyles,
    )
    cameraResolutionXYLabel.textContent = `cameraResolutionXYVpx: [${cameraResolutionXY[0]}, ${cameraResolutionXY[1]}]`
    cameraResolutionXYLabel.style.left = `${labelLeft}px`
    cameraResolutionXYLabel.style.top = '20px'
  }

  // 1b. screenResolutionXYVpx label (top 50px)
  if (
    screenResolutionXYVpx !== null &&
    Array.isArray(screenResolutionXYVpx) &&
    screenResolutionXYVpx[0] &&
    screenResolutionXYVpx[1]
  ) {
    screenResolutionXYVpxLabel = createOrUpdateElement(
      screenResolutionXYVpxLabel,
      'rc-screen-resolution-xy-vpx-label',
      labelBaseStyles,
    )
    screenResolutionXYVpxLabel.textContent = `screenResolutionXYVpx: [${screenResolutionXYVpx[0]}, ${screenResolutionXYVpx[1]}]`
    screenResolutionXYVpxLabel.style.left = `${labelLeft}px`
    screenResolutionXYVpxLabel.style.top = '50px'
  }

  // 2. pxPerCm label (top 80px)
  if (pxPerCm !== null && pxPerCm !== undefined) {
    pxPerCmLabel = createOrUpdateElement(
      pxPerCmLabel,
      'rc-px-per-cm-label',
      labelBaseStyles,
    )
    pxPerCmLabel.textContent = `pxPerCm: ${pxPerCm.toFixed(1)}`
    pxPerCmLabel.style.left = `${labelLeft}px`
    pxPerCmLabel.style.top = '80px'
  }

  // Check if enough time has passed to update dynamic labels (throttle for readability)
  const currentTime = Date.now()
  const shouldUpdateLabels =
    currentTime - lastLabelUpdateTime >= LABEL_UPDATE_INTERVAL_MS
  if (shouldUpdateLabels) {
    lastLabelUpdateTime = currentTime
  }

  // 3. ipdCm label (top 110px)
  if (ipdCm !== null && ipdCm !== undefined) {
    ipdCmLabel = createOrUpdateElement(
      ipdCmLabel,
      'rc-ipd-cm-label',
      labelBaseStyles,
    )
    ipdCmLabel.textContent = `ipdCm: ${ipdCm.toFixed(1)} cm`
    ipdCmLabel.style.left = `${labelLeft}px`
    ipdCmLabel.style.top = '110px'
  }

  // 4. ipdOverWidth label (top 140px)
  {
    const ipdValue = ipdOverWidth ?? averageDist
    if (ipdValue !== undefined) {
      ipdLabel = createOrUpdateElement(
        ipdLabel,
        'rc-ipd-label',
        labelBaseStyles,
      )
      if (shouldUpdateLabels) {
        ipdLabel.textContent = `ipdOverWidth: ${ipdValue.toFixed(4)}`
      }
      ipdLabel.style.left = `${labelLeft}px`
      ipdLabel.style.top = '140px'
    }
  }

  // 5. fOverWidth label (top 170px)
  if (fOverWidth !== null && fOverWidth !== undefined) {
    fOverWidthLabel = createOrUpdateElement(
      fOverWidthLabel,
      'rc-f-over-width-label',
      labelBaseStyles,
    )
    if (shouldUpdateLabels) {
      fOverWidthLabel.textContent = `fOverWidth: ${fOverWidth.toFixed(4)}`
    }
    fOverWidthLabel.style.left = `${labelLeft}px`
    fOverWidthLabel.style.top = '170px'
  }

  // 6. imageBasedEyesToFootCm label (top 200px)
  if (imageBasedEyesToFootCm !== null && imageBasedEyesToFootCm !== undefined) {
    imageBasedEyesToFootCmLabel = createOrUpdateElement(
      imageBasedEyesToFootCmLabel,
      'rc-image-based-eyes-to-foot-cm-label',
      labelBaseStyles,
    )
    if (shouldUpdateLabels) {
      imageBasedEyesToFootCmLabel.textContent = `imageBasedEyesToFootCm: ${imageBasedEyesToFootCm.toFixed(decimalPlace || 1)} cm`
    }
    imageBasedEyesToFootCmLabel.style.left = `${labelLeft}px`
    imageBasedEyesToFootCmLabel.style.top = '200px'
  }

  // 7. imageBasedEyesToPointCm label (top 230px)
  if (
    imageBasedEyesToPointCm !== null &&
    imageBasedEyesToPointCm !== undefined
  ) {
    imageBasedEyesToPointCmLabel = createOrUpdateElement(
      imageBasedEyesToPointCmLabel,
      'rc-image-based-eyes-to-point-cm-label',
      labelBaseStyles,
    )
    if (shouldUpdateLabels) {
      imageBasedEyesToPointCmLabel.textContent = `imageBasedEyesToPointCm: ${imageBasedEyesToPointCm.toFixed(decimalPlace || 1)} cm`
    }
    imageBasedEyesToPointCmLabel.style.left = `${labelLeft}px`
    imageBasedEyesToPointCmLabel.style.top = '230px'
  }

  // 7b. rulerBasedEyesToFootCm label (top 260px)
  if (rulerBasedEyesToFootCm !== null && rulerBasedEyesToFootCm !== undefined) {
    rulerBasedEyesToFootCmLabel = createOrUpdateElement(
      rulerBasedEyesToFootCmLabel,
      'rc-ruler-based-eyes-to-foot-cm-label',
      labelBaseStyles,
    )
    rulerBasedEyesToFootCmLabel.textContent = `rulerBasedEyesToFootCm: ${rulerBasedEyesToFootCm.toFixed(decimalPlace || 1)} cm`
    rulerBasedEyesToFootCmLabel.style.left = `${labelLeft}px`
    rulerBasedEyesToFootCmLabel.style.top = '260px'
  }

  // 7c. rulerBasedEyesToPointCm label (top 290px)
  if (
    rulerBasedEyesToPointCm !== null &&
    rulerBasedEyesToPointCm !== undefined
  ) {
    rulerBasedEyesToPointCmLabel = createOrUpdateElement(
      rulerBasedEyesToPointCmLabel,
      'rc-ruler-based-eyes-to-point-cm-label',
      labelBaseStyles,
    )
    rulerBasedEyesToPointCmLabel.textContent = `rulerBasedEyesToPointCm: ${rulerBasedEyesToPointCm.toFixed(decimalPlace || 1)} cm`
    rulerBasedEyesToPointCmLabel.style.left = `${labelLeft}px`
    rulerBasedEyesToPointCmLabel.style.top = '290px'
  }

  // 8. footToPointCm label (top 320px)
  if (footXYPx !== null && pointXYPx !== null && pxPerCm !== null) {
    footToPointCmLabel = createOrUpdateElement(
      footToPointCmLabel,
      'rc-foot-to-point-cm-label',
      labelBaseStyles,
    )
    const footToPointCm =
      Math.sqrt(
        (footXYPx[0] - pointXYPx[0]) ** 2 + (footXYPx[1] - pointXYPx[1]) ** 2,
      ) / pxPerCm
    footToPointCmLabel.textContent = `footToPointCm: ${footToPointCm.toFixed(decimalPlace || 1)} cm`
    footToPointCmLabel.style.left = `${labelLeft}px`
    footToPointCmLabel.style.top = '320px'
  }

  // 9. footXYPx label (top 350px)
  if (footXYPx !== null && footXYPx !== undefined) {
    footXYPxLabel = createOrUpdateElement(
      footXYPxLabel,
      'rc-foot-xy-px-label',
      labelBaseStyles,
    )
    if (shouldUpdateLabels) {
      const xyText = Array.isArray(footXYPx)
        ? `[${Math.round(footXYPx[0])}, ${Math.round(footXYPx[1])}]`
        : `${footXYPx}`
      footXYPxLabel.textContent = `footXYPx: ${xyText}`
    }
    footXYPxLabel.style.left = `${labelLeft}px`
    footXYPxLabel.style.top = '350px'
  }

  // 10. pointXYPx label (top 380px)
  if (pointXYPx !== null && pointXYPx !== undefined) {
    pointXYPxLabel = createOrUpdateElement(
      pointXYPxLabel,
      'rc-point-xy-px-label',
      labelBaseStyles,
    )
    const xyText = Array.isArray(pointXYPx)
      ? `[${Math.round(pointXYPx[0])}, ${Math.round(pointXYPx[1])}]`
      : `${pointXYPx}`
    pointXYPxLabel.textContent = `pointXYPx: ${xyText}`
    pointXYPxLabel.style.left = `${labelLeft}px`
    pointXYPxLabel.style.top = '380px'
  }

  // 11. viewingDistanceWhichEye label (top 410px)
  if (viewingDistanceWhichEye !== undefined) {
    viewingDistanceWhichEyeLabel = createOrUpdateElement(
      viewingDistanceWhichEyeLabel,
      'rc-viewing-distance-which-eye-label',
      labelBaseStyles,
    )

    // Calculate viewing distance based on eye selection
    const calculateViewingDistanceCm = (
      eyeSelection,
      distanceCm_left,
      distanceCm_right,
    ) => {
      switch (eyeSelection) {
        case 'left':
          return distanceCm_left
        case 'right':
          return distanceCm_right
        case 'min':
          return Math.min(distanceCm_left, distanceCm_right)
        case 'geoMean':
          return Math.sqrt(distanceCm_left * distanceCm_right)
        default:
          return 'NaN'
      }
    }

    const viewingDistanceCm = calculateViewingDistanceCm(
      viewingDistanceWhichEye,
      distanceCm_left,
      distanceCm_right,
    )

    if (shouldUpdateLabels) {
      viewingDistanceWhichEyeLabel.textContent = `viewingDistanceWhichEye: ${viewingDistanceWhichEye} (${typeof viewingDistanceCm === 'number' ? viewingDistanceCm.toFixed(1) : viewingDistanceCm} cm)`
    }
    viewingDistanceWhichEyeLabel.style.left = `${labelLeft}px`
    viewingDistanceWhichEyeLabel.style.top = '410px'
  }

  // 12. viewingDistanceWhichPoint label (top 440px)
  if (viewingDistanceWhichPoint !== undefined) {
    viewingDistanceWhichPointLabel = createOrUpdateElement(
      viewingDistanceWhichPointLabel,
      'rc-viewing-distance-which-point-label',
      labelBaseStyles,
    )

    // Interpret viewingDistanceWhichPoint based on category
    const interpretViewingDistanceWhichPoint = (
      category,
      nearestXYPx,
      cameraXYPx,
      viewingDistanceWhichEye,
    ) => {
      let pointXYDeg
      switch (category) {
        case 'fixation':
          pointXYDeg = [0, 0]
          break
        case 'target':
          pointXYDeg = NaN // TODO
          break
        case 'nearest':
          pointXYDeg = NaN // TODO nearestXYDeg
          break
        case 'camera':
          pointXYDeg = NaN // TODO XYDegOfPx(cameraXYPx);
          break
        case 'xyDeg':
          pointXYDeg = NaN // TODO viewingDistanceToXYDeg;
          break
        default:
          pointXYDeg = [
            viewingDistanceWhichPoint[0],
            viewingDistanceWhichPoint[1],
          ]
      }
      return pointXYDeg
    }

    const interpretedValue = interpretViewingDistanceWhichPoint(
      viewingDistanceWhichPoint,
      nearestXYPx,
      cameraXYPx,
      viewingDistanceWhichEye,
    )

    let displayValue
    if (Array.isArray(interpretedValue)) {
      displayValue = `[${interpretedValue.join(', ')}]`
    } else {
      displayValue = interpretedValue
    }

    viewingDistanceWhichPointLabel.textContent = `viewingDistanceWhichPoint: ${displayValue}`
    viewingDistanceWhichPointLabel.style.left = `${labelLeft}px`
    viewingDistanceWhichPointLabel.style.top = '440px'
  }

  // ── Head rotation labels ───────────────────────────────────────────
  if (headYawDeg != null) {
    headYawLabel = createOrUpdateElement(
      headYawLabel,
      'rc-head-yaw-label',
      labelBaseStyles,
    )
    if (shouldUpdateLabels) {
      headYawLabel.textContent = `headYawDeg: ${headYawDeg.toFixed(1)}°`
    }
    headYawLabel.style.left = `${labelLeft}px`
    headYawLabel.style.top = '470px'
  }

  if (headIpdShrinkage != null) {
    ipdShrinkageLabel = createOrUpdateElement(
      ipdShrinkageLabel,
      'rc-ipd-shrinkage-label',
      labelBaseStyles,
    )
    if (shouldUpdateLabels) {
      ipdShrinkageLabel.textContent = `ipdShrinkage: ${headIpdShrinkage.toFixed(4)}`
    }
    ipdShrinkageLabel.style.left = `${labelLeft}px`
    ipdShrinkageLabel.style.top = '500px'
  }

  // ── Head rotation dot ──────────────────────────────────────────────
  // Green dot on screen showing where the head is pointing relative to
  // the eye midpoint. Uses yaw and pitch scaled by the current distance
  // estimate to project a screen position.
  if (headYawDeg != null && pxPerCm != null && nearestLeft && nearestRight) {
    headRotationDot = createOrUpdateElement(
      headRotationDot,
      'rc-head-rotation-dot',
      {
        position: 'fixed',
        width: '14px',
        height: '14px',
        background: 'lime',
        border: '2px solid black',
        borderRadius: '50%',
        zIndex: '99999999999999',
        pointerEvents: 'none',
      },
    )

    // Eye midpoint in screen px (average of left and right foot points)
    const eyeMidScreenX = (nearestLeft[0] + nearestRight[0]) / 2
    const eyeMidScreenY = (nearestLeft[1] + nearestRight[1]) / 2

    // Use the average reported distance (from either eye) as the
    // depth for projecting the angle onto the screen plane.
    const avgDistCm =
      distanceLeft != null && distanceRight != null
        ? (distanceLeft + distanceRight) / 2
        : (distanceLeft ?? distanceRight ?? 50)

    const yawRad = (headYawDeg * Math.PI) / 180
    const dxCm = avgDistCm * Math.tan(yawRad)
    const dotX = eyeMidScreenX + dxCm * pxPerCm
    const dotY = eyeMidScreenY

    const clampedX = Math.max(0, Math.min(dotX, window.innerWidth - 14))
    const clampedY = Math.max(0, Math.min(dotY, window.innerHeight - 14))

    headRotationDot.style.left = `${clampedX - 7}px`
    headRotationDot.style.top = `${clampedY - 7}px`
  }
}

const cleanUpEyePoints = () => {
  //clean up all points drawn in _drawNearestPoints
  if (nearestPointDots.left) {
    document.body.removeChild(nearestPointDots.left)
    nearestPointDots.left = null
  }
  if (nearestPointDots.right) {
    document.body.removeChild(nearestPointDots.right)
    nearestPointDots.right = null
  }
  if (nearestPointLabels.left) {
    document.body.removeChild(nearestPointLabels.left)
    nearestPointLabels.left = null
  }
  if (nearestPointLabels.right) {
    document.body.removeChild(nearestPointLabels.right)
    nearestPointLabels.right = null
  }
  if (cameraResolutionXYLabel) {
    document.body.removeChild(cameraResolutionXYLabel)
    cameraResolutionXYLabel = null
  }
  if (screenResolutionXYVpxLabel) {
    document.body.removeChild(screenResolutionXYVpxLabel)
    screenResolutionXYVpxLabel = null
  }
  if (pxPerCmLabel) {
    document.body.removeChild(pxPerCmLabel)
    pxPerCmLabel = null
  }
  if (ipdCmLabel) {
    document.body.removeChild(ipdCmLabel)
    ipdCmLabel = null
  }
  if (ipdLabel) {
    document.body.removeChild(ipdLabel)
    ipdLabel = null
  }
  if (fOverWidthLabel) {
    document.body.removeChild(fOverWidthLabel)
    fOverWidthLabel = null
  }
  if (imageBasedEyesToFootCmLabel) {
    document.body.removeChild(imageBasedEyesToFootCmLabel)
    imageBasedEyesToFootCmLabel = null
  }
  if (imageBasedEyesToPointCmLabel) {
    document.body.removeChild(imageBasedEyesToPointCmLabel)
    imageBasedEyesToPointCmLabel = null
  }
  if (rulerBasedEyesToFootCmLabel) {
    document.body.removeChild(rulerBasedEyesToFootCmLabel)
    rulerBasedEyesToFootCmLabel = null
  }
  if (rulerBasedEyesToPointCmLabel) {
    document.body.removeChild(rulerBasedEyesToPointCmLabel)
    rulerBasedEyesToPointCmLabel = null
  }
  if (footToPointCmLabel) {
    document.body.removeChild(footToPointCmLabel)
    footToPointCmLabel = null
  }
  if (footXYPxLabel) {
    document.body.removeChild(footXYPxLabel)
    footXYPxLabel = null
  }
  if (pointXYPxLabel) {
    document.body.removeChild(pointXYPxLabel)
    pointXYPxLabel = null
  }
  if (viewingDistanceWhichEyeLabel) {
    document.body.removeChild(viewingDistanceWhichEyeLabel)
    viewingDistanceWhichEyeLabel = null
  }
  if (viewingDistanceWhichPointLabel) {
    document.body.removeChild(viewingDistanceWhichPointLabel)
    viewingDistanceWhichPointLabel = null
  }
  if (headYawLabel) {
    document.body.removeChild(headYawLabel)
    headYawLabel = null
  }
  if (ipdShrinkageLabel) {
    document.body.removeChild(ipdShrinkageLabel)
    ipdShrinkageLabel = null
  }
  if (headRotationDot) {
    document.body.removeChild(headRotationDot)
    headRotationDot = null
  }
  if (nearestPointCoordsLabels.left) {
    document.body.removeChild(nearestPointCoordsLabels.left)
    nearestPointCoordsLabels.left = null
  }
  if (nearestPointCoordsLabels.right) {
    document.body.removeChild(nearestPointCoordsLabels.right)
    nearestPointCoordsLabels.right = null
  }
  if (pupilDots.left) {
    document.body.removeChild(pupilDots.left)
    pupilDots.left = null
  }
  if (pupilDots.right) {
    document.body.removeChild(pupilDots.right)
    pupilDots.right = null
  }
  if (eyePointDots.left) {
    document.body.removeChild(eyePointDots.left)
    eyePointDots.left = null
  }
  if (eyePointDots.right) {
    document.body.removeChild(eyePointDots.right)
    eyePointDots.right = null
  }
}

const _getNearPoint = (
  RC,
  trackingOptions,
  video,
  mesh,
  averageDist,
  timestamp,
  ppi,
  latency,
) => {
  const offsetToVideoCenter = cyclopean(
    video,
    {
      x: mesh[468].x,
      y: mesh[468].y,
      z: mesh[468].z,
    },
    {
      x: mesh[473].x,
      y: mesh[473].y,
      z: mesh[473].z,
    },
  )
  offsetToVideoCenter.forEach((offset, i) => {
    // Average inter-pupillary distance - 6.4cm
    offsetToVideoCenter[i] =
      ((RC.PDCm ? RC.PDCm.value : RC._CONST.N.PD_DONT_USE) * offset) /
      averageDist
  })

  const nPData = {
    value: {
      x: toFixedNumber(offsetToVideoCenter[0], trackingOptions.decimalPlace),
      y: toFixedNumber(
        offsetToVideoCenter[1] + ((screen.height / 2) * 2.54) / ppi, // Commonly the webcam is 0.5cm above the screen
        trackingOptions.decimalPlace,
      ),
      latencyMs: latency,
    },
    timestamp: timestamp,
  }

  RC.newNearPointData = nPData

  // SHOW
  const dotR = 5
  if (trackingOptions.showNearPoint) {
    const offsetX = (nPData.value.x * ppi) / 2.54
    const offsetY = (nPData.value.y * ppi) / 2.54
    Object.assign(nearPointDot.style, {
      left: `${screen.width / 2 - window.screenLeft + offsetX - dotR}px`,
      top: `${
        screen.height / 2 -
        window.screenTop -
        (window.outerHeight - window.innerHeight) -
        offsetY -
        dotR
      }px`,
    })
  }

  return nPData
}

RemoteCalibrator.prototype.pauseDistance = function () {
  if (
    this.gazeTracker.checkInitialized('distance', true) &&
    !this._trackingPaused.distance
  ) {
    iRepeatOptions.break = true
    if (rafId) {
      cancelAnimationFrame(rafId)
      rafId = null
    }

    // Stop calibration-phase iris tracking if still active
    if (this._stopIrisTracking) {
      this._stopIrisTracking()
      this._stopIrisTracking = null
    }

    stopIrisDrawing()
    if (nearPointDot) nearPointDot.style.display = 'none'
    this._trackingVideoFrameTimestamps.distance = 0

    this._trackingPaused.distance = true
    this.pauseNudger() // 0.6.0

    return this
  }
  return null
}

RemoteCalibrator.prototype.resumeDistance = function (showIrisesBool = false) {
  if (
    this.gazeTracker.checkInitialized('distance', true) &&
    this._trackingPaused.distance
  ) {
    iRepeatOptions.break = false
    // Use the parameter or fall back to trackingOptions
    if (showIrisesBool || trackingOptions.showIrisesBool) startIrisDrawing(this)
    if (nearPointDot) nearPointDot.style.display = 'block'

    this._trackingVideoFrameTimestamps.distance = 0

    // Restart the requestAnimationFrame loop
    const renderPrediction = async currentTime => {
      if (iRepeatOptions.break) {
        return
      }

      // Throttle to 30fps
      if (currentTime - lastFrameTime >= frameInterval) {
        // Note: We need to reconstruct the render function parameters here
        // This is a simplified resume - full implementation would need to store these
        lastFrameTime = currentTime
      }
      rafId = requestAnimationFrame(renderPrediction)
    }

    rafId = requestAnimationFrame(renderPrediction)

    this._trackingPaused.distance = false
    this.resumeNudger() // 0.6.0

    return this
  }
  return null
}

RemoteCalibrator.prototype.endDistance = function (
  endAll = false,
  _r = true,
  preserveVideo = false,
) {
  if (this.gazeTracker.checkInitialized('distance', true)) {
    iRepeatOptions.break = true
    if (rafId) {
      cancelAnimationFrame(rafId)
      rafId = null
    }

    // Stop calibration-phase iris tracking if still active
    if (this._stopIrisTracking) {
      this._stopIrisTracking()
      this._stopIrisTracking = null
    }

    stopIrisDrawing()

    // Clean up iris canvas
    if (irisCanvas) {
      document.body.removeChild(irisCanvas)
      irisCanvas = null
      irisCtx = null
    }
    RC_instance = null

    iRepeatOptions.framerate = 20

    trackingOptions.pipWidthPx = 0
    trackingOptions.decimalPlace = 2
    trackingOptions.framerate = 30
    trackingOptions.nearPoint = true
    trackingOptions.showNearPoint = false

    trackingOptions.desiredDistanceCm = undefined
    trackingOptions.desiredDistanceTolerance = 1.2
    trackingOptions.desiredDistanceMonitor = false
    trackingOptions.desiredDistanceMonitorCancelable = false
    trackingOptions.desiredDistanceMonitorAllowRecalibrate = true
    trackingOptions.calibrateDistanceCorrectForHeadRotation = 'none'
    trackingOptions.viewingDistanceAllowedHeadRotationDeg = 180

    stdDist.current = null
    stdFactor = null

    video = null

    readyToGetFirstData = false
    this._trackingVideoFrameTimestamps.distance = 0
    this._trackingPaused.distance = false

    // Near point
    if (nearPointDot) {
      document.body.removeChild(nearPointDot)
      nearPointDot = null
    }

    // Debug nearest points cleanup
    if (nearestPointDots.left) {
      document.body.removeChild(nearestPointDots.left)
      nearestPointDots.left = null
    }
    if (nearestPointDots.right) {
      document.body.removeChild(nearestPointDots.right)
      nearestPointDots.right = null
    }
    if (nearestPointLabels.left) {
      document.body.removeChild(nearestPointLabels.left)
      nearestPointLabels.left = null
    }
    if (nearestPointLabels.right) {
      document.body.removeChild(nearestPointLabels.right)
      nearestPointLabels.right = null
    }
    if (cameraResolutionXYLabel) {
      document.body.removeChild(cameraResolutionXYLabel)
      cameraResolutionXYLabel = null
    }
    if (screenResolutionXYVpxLabel) {
      document.body.removeChild(screenResolutionXYVpxLabel)
      screenResolutionXYVpxLabel = null
    }
    if (pxPerCmLabel) {
      document.body.removeChild(pxPerCmLabel)
      pxPerCmLabel = null
    }
    if (ipdCmLabel) {
      document.body.removeChild(ipdCmLabel)
      ipdCmLabel = null
    }
    if (ipdLabel) {
      document.body.removeChild(ipdLabel)
      ipdLabel = null
    }
    if (fOverWidthLabel) {
      document.body.removeChild(fOverWidthLabel)
      fOverWidthLabel = null
    }
    if (imageBasedEyesToFootCmLabel) {
      document.body.removeChild(imageBasedEyesToFootCmLabel)
      imageBasedEyesToFootCmLabel = null
    }
    if (imageBasedEyesToPointCmLabel) {
      document.body.removeChild(imageBasedEyesToPointCmLabel)
      imageBasedEyesToPointCmLabel = null
    }
    if (rulerBasedEyesToFootCmLabel) {
      document.body.removeChild(rulerBasedEyesToFootCmLabel)
      rulerBasedEyesToFootCmLabel = null
    }
    if (rulerBasedEyesToPointCmLabel) {
      document.body.removeChild(rulerBasedEyesToPointCmLabel)
      rulerBasedEyesToPointCmLabel = null
    }
    if (footToPointCmLabel) {
      document.body.removeChild(footToPointCmLabel)
      footToPointCmLabel = null
    }
    if (footXYPxLabel) {
      document.body.removeChild(footXYPxLabel)
      footXYPxLabel = null
    }
    if (pointXYPxLabel) {
      document.body.removeChild(pointXYPxLabel)
      pointXYPxLabel = null
    }
    if (viewingDistanceWhichEyeLabel) {
      document.body.removeChild(viewingDistanceWhichEyeLabel)
      viewingDistanceWhichEyeLabel = null
    }
    if (viewingDistanceWhichPointLabel) {
      document.body.removeChild(viewingDistanceWhichPointLabel)
      viewingDistanceWhichPointLabel = null
    }
    if (headYawLabel) {
      document.body.removeChild(headYawLabel)
      headYawLabel = null
    }
    if (ipdShrinkageLabel) {
      document.body.removeChild(ipdShrinkageLabel)
      ipdShrinkageLabel = null
    }
    if (headRotationDot) {
      document.body.removeChild(headRotationDot)
      headRotationDot = null
    }
    if (nearestPointCoordsLabels.left) {
      document.body.removeChild(nearestPointCoordsLabels.left)
      nearestPointCoordsLabels.left = null
    }
    if (nearestPointCoordsLabels.right) {
      document.body.removeChild(nearestPointCoordsLabels.right)
      nearestPointCoordsLabels.right = null
    }

    if (eyePointDots && eyePointDots.left) {
      document.body.removeChild(eyePointDots.left)
      eyePointDots.left = null
    }
    if (eyePointDots && eyePointDots.right) {
      document.body.removeChild(eyePointDots.right)
      eyePointDots.right = null
    }

    // Nudger
    this.endNudger()

    this._distanceTrackingFullyInitialized = false
    if (_r) this.gazeTracker.end('distance', endAll, preserveVideo)
    return this
  }
  return null
}

RemoteCalibrator.prototype.getDistanceNow = async function (callback = null) {
  if (
    !this.checkInitialized() ||
    !this.gazeTracker.checkInitialized('distance', true) ||
    !iRepeatOptions.break
  )
    return

  const c = callback || this.gazeTracker.defaultDistanceTrackCallback

  const v = document.getElementById('webgazerVideoCanvas')
  const m = await this.gazeTracker.webgazer.getTracker().model
  const videoTimestamp = performance.now()
  const f = await m.estimateFaces(v)

  if (f.length) {
    const mesh = f[0].scaledMesh
    const useZ = trackingOptions.calibrateDistanceIpdUsesZBool !== false
    const calibrateDistanceCorrectForHeadRotation =
      trackingOptions.calibrateDistanceCorrectForHeadRotation
    const leftEyePt = {
      x: mesh[468].x,
      y: mesh[468].y,
      z: mesh[468].z,
    }
    const rightEyePt = {
      x: mesh[473].x,
      y: mesh[473].y,
      z: mesh[473].z,
    }
    const dist = eyeDist(leftEyePt, rightEyePt, useZ)

    const { ipdShrinkage: shrinkageNow } = estimateHeadYawRobust(
      mesh,
      leftEyePt,
      rightEyePt,
      calibrateDistanceCorrectForHeadRotation,
    )
    const correctedDist = dist / shrinkageNow

    const timestamp = performance.now()
    //
    const latency = timestamp - videoTimestamp
    //

    // Calculate webcam-to-eye distance (using head-rotation-corrected IPD)
    const webcamToEyeDistance = stdFactor / correctedDist

    // Apply trigonometric adjustment to get screen-center-to-eye distance
    const ppi = this.screenPpi
      ? this.screenPpi.value
      : this._CONST.N.PPI_DONT_USE
    const screenCenterToEyeDistance = _adjustDistanceToScreenCenter(
      webcamToEyeDistance,
      ppi,
    )

    this.newViewingDistanceData = {
      value: toFixedNumber(
        screenCenterToEyeDistance,
        trackingOptions.decimalPlace,
      ),
      timestamp: timestamp,
      method: this._CONST.VIEW_METHOD.F,
      latencyMs: latency,
    }
    const data = this.newViewingDistanceData

    let nPData
    if (trackingOptions.nearPoint) {
      nPData = _getNearPoint(
        this,
        trackingOptions,
        v,
        mesh,
        dist,
        timestamp,
        this.screenPpi ? this.screenPpi.value : this._CONST.N.PPI_DONT_USE,
        latency,
      )
    }

    safeExecuteFunc(c, {
      value: {
        viewingDistanceCm: data.value,
        nearPointCm: nPData ? nPData.value : null,
        latencyMs: latency,
      },
      timestamp: timestamp,
      method: this._CONST.VIEW_METHOD.F,
    })
    return data
  }

  return null
}

RemoteCalibrator.prototype.showNearPoint = function (show = true) {
  if (this.gazeTracker.checkInitialized('distance', false)) {
    const n = document.querySelector('#rc-near-point-dot')
    if (n) n.display = show ? 'block' : 'none'
  }
}

const _calculateDistanceFromCenterToTop = ppi => {
  // get the screen height in pixels
  const screenHeightPixels = window.screen.height

  // calculate half the screen height in pixels
  const halfScreenHeightPixels = screenHeightPixels / 2

  // convert pixels to inches using the ppi
  const halfScreenHeightInches = halfScreenHeightPixels / ppi

  // convert inches to centimeters (1 inch = 2.54 cm)
  const halfScreenHeightCm = halfScreenHeightInches * 2.54

  return halfScreenHeightCm
}

// Helper function to convert webcam-to-eye distance to screen-center-to-eye distance
const _adjustDistanceToScreenCenter = (webcamToEyeDistance, ppi) => {
  // Calculate distance from webcam to screen center (half screen height)
  const webcamToScreenCenterDistance = _calculateDistanceFromCenterToTop(ppi)

  // Use Pythagorean theorem: screen-center-to-eye = sqrt(webcam-to-eye² + webcam-to-screen-center²)
  const screenCenterToEyeDistance = Math.sqrt(
    Math.pow(webcamToEyeDistance, 2) -
      Math.pow(webcamToScreenCenterDistance, 2),
  )

  return screenCenterToEyeDistance
}
